import {
  CertificationStatusDto,
  CommissionCandidateRoleDto,
  DevelopmentCycleCampaignStatusDto,
  ExamDayStatusDto,
  ExamStageStatusDto,
  IndividualExamStatusDto,
  NewsCategoryDto,
  NewsStatusDto,
  PracticalExamAttemptStatusDto,
  ResourceTypeDto,
  SurveyAssignmentStatusDto,
} from '@/api/generated';
import { AdminPanelTranslation } from '@/assets/locales/views/adminPanel/adminPanel.translation.en.ts';

export const adminPanelTranslation: AdminPanelTranslation = {
  certifications: {
    adminInstructions: {
      closeButton: 'Zamknij',
      drawerTitle: 'Instrukcja dla administratora',
      sectionTitles: {
        adminSteps: 'Co robi administrator',
        employeeView: 'Co i kiedy widzi pracownik',
        purpose: 'Cel etapu',
        rules: 'Zasady',
        transition: 'Przejście do kolejnego etapu',
      },
      stages: {
        developmentCenter: {
          adminSteps:
            '1. Uruchom etap Development Center — etap nie ma własnej nazwy, certyfikacja albo obejmuje DC, albo nie.\n\n2. Ustaw oczekiwany wynik w skali z pliku — bez niego ocena uczestnika nie ma punktu odniesienia.\n\n3. Ustaw datę, od której uczestnicy widzą swoje raporty.\n\n4. Wgraj arkusz z wynikami sesji. Jeśli ktoś z arkusza nie brał udziału w teście wiedzy ani egzaminie praktycznym, dostaniesz uwagę — import i tak wejdzie, jeśli zdecydujesz się kontynuować.\n\n5. Wgraj paczkę ZIP z raportami PDF uczestników.',
          employeeView:
            'Uczestnik widzi kafelek etapu Development Center w swojej ścieżce certyfikacji.\n\nSwój raport PDF pobierze dopiero od ustawionej daty i tylko wtedy, gdy raport został dla niego wgrany.\n\nJeśli certyfikacja nie obejmuje Development Center, uczestnik widzi wyłącznie informację o tym fakcie.',
          purpose:
            'Development Center to sesja oceny kompetencji menedżerskich prowadzona przez asesorów poza aplikacją. Wyniki i raport indywidualny trafiają do systemu i zasilają zarówno ścieżkę certyfikacji, jak i plan rozwoju pracownika.',
          rules:
            'Etap jest opcjonalny — certyfikacja może go nie obejmować.\n\nWyniki są powiązane z konkretną certyfikacją: ten sam pracownik może mieć wynik DC w kilku certyfikacjach.\n\nEtap jest zaliczony, gdy uczestnik ma wgrany aktualny wynik sesji.\n\nPonowne wgranie arkusza dla tej samej certyfikacji tworzy nową wersję wyników.',
          transition:
            'Wyniki Development Center są wykorzystywane przy tworzeniu planu rozwoju — ostatniego etapu certyfikacji.',
        },
        exam: {
          adminSteps:
            '1. Zainicjuj etap — nadaj mu nazwę i utwórz rekord egzaminu praktycznego.\n\n2. Skonfiguruj egzamin — dodaj pytania/zadania, ustaw próg zdawalności i czas trwania.\n\n3. Utwórz dni egzaminacyjne — wyznacz terminy i sloty czasowe, w których odbywają się egzaminy indywidualne.\n\n4. Przypisz uczestników do dni egzaminacyjnych i do konkretnych slotów. Tylko przypisani uczestnicy mogą podejść do egzaminu.\n\n5. W trakcie egzaminu egzaminator ocenia wykonanie zadań przez pracownika bezpośrednio w systemie.\n\n6. Zmień status etapu (Draft → Active → Completed) używając przycisku akcji w prawym górnym rogu karty etapu.',
          employeeView:
            'Pracownik widzi etap egzaminu praktycznego dopiero wtedy, gdy etap ma status „Aktywny" i pracownik jest przypisany do dnia egzaminacyjnego.\n\nW panelu pracownika pojawia się termin egzaminu (data, godzina, lokalizacja) oraz informacja o przypisanym egzaminatorze.\n\nPo egzaminie pracownik widzi swój wynik i status (zdany / niezdany) gdy egzaminator zakończy ocenę.',
          purpose:
            'Egzamin praktyczny weryfikuje umiejętności pracownika w realnych zadaniach zawodowych. To ostatni krok potwierdzający faktyczne kompetencje — teoria z testu wiedzy musi przełożyć się na praktykę.',
          rules:
            'Egzamin praktyczny jest prowadzony przez wyznaczonego egzaminatora, który ocenia wykonanie zadań.\n\nUczestnik musi być przypisany do konkretnego dnia egzaminacyjnego, aby móc do niego przystąpić.\n\nWynik egzaminu (zdany/niezdany) jest ustalany przez egzaminatora i nie podlega automatycznemu zaliczeniu.\n\nEtap może mieć status: Niezainicjowany, Niewystartowany, W toku, Ukończony.',
          transition:
            'Pomyślne ukończenie egzaminu praktycznego jest warunkiem uzyskania certyfikacji. Po zakończeniu etapu system odnotowuje pracownika jako certyfikowanego.\n\nKolejnym krokiem (gdy dostępny) jest Plan Rozwoju, który uruchamiany jest na podstawie luk zidentyfikowanych w poprzednich etapach.',
        },
        plan: {
          adminSteps:
            'Funkcjonalność Planu Rozwoju jest w przygotowaniu. Po udostępnieniu administrator będzie mógł:\n\n1. Wygenerować plan rozwoju na podstawie luk zidentyfikowanych w samoocenie, teście wiedzy i egzaminie praktycznym.\n\n2. Przypisać pracownikowi konkretne działania rozwojowe (szkolenia, zadania, mentoring).\n\n3. Monitorować postępy i zamykać poszczególne aktywności.',
          employeeView:
            'Po uruchomieniu funkcjonalności pracownik zobaczy swój indywidualny plan rozwoju z listą zaleconych działań, terminami i statusem realizacji każdego z zadań.',
          purpose:
            'Plan Rozwoju to etap poegzaminacyjny — na jego podstawie pracownik otrzymuje indywidualne wytyczne dotyczące dalszego rozwoju kompetencji. Etap zamyka proces certyfikacji i inicjuje rozwój pracownika w obszarach, w których wykryto braki.',
          rules:
            'Etap Planu Rozwoju jest obecnie niedostępny (funkcjonalność w budowie).\n\nPo jego udostępnieniu plan będzie generowany na podstawie wyników wcześniejszych etapów i historii pracownika.',
          transition:
            'Plan Rozwoju jest ostatnim krokiem procesu certyfikacji. Po jego ukończeniu cały proces certyfikacyjny pracownika zostaje zamknięty.',
        },
        survey: {
          adminSteps:
            '1. Przypisz ankietę do certyfikacji — możesz wybrać istniejącą lub utworzyć nową w dialogu „Przypisz ankietę".\n\n2. Ustaw termin wypełnienia (opcjonalnie) — pracownicy będą widzieć deadline w swoim panelu.\n\n3. Śledź postępy w sekcji Uczestnicy — widzisz, kto ankietę wypełnił, a kto jeszcze nie.\n\n4. W razie potrzeby odłącz ankietę (przycisk „Odłącz") — uwaga: traci się wtedy dotychczasowe odpowiedzi.',
          employeeView:
            'Pracownik widzi ankietę w swoim panelu w momencie, gdy zostanie przypisany jako uczestnik certyfikacji, a do certyfikacji przypisano ankietę samooceny.\n\nPracownik wypełnia ją przed przystąpieniem do testu wiedzy — odpowiedzi są zapisywane i wykorzystywane do identyfikacji luk kompetencyjnych.\n\nJeśli ustawiono termin, pracownik widzi deadline i powiadomienia przypominające.',
          purpose:
            'Samoocena to pierwszy krok procesu certyfikacji. Pozwala pracownikowi samodzielnie ocenić swój poziom kompetencji przed właściwą weryfikacją wiedzy. Dzięki temu ustalamy punkt wyjściowy i identyfikujemy obszary wymagające rozwoju.',
          rules:
            'Samoocena jest ankietą wypełnianą przez pracownika — nie podlega ocenie przez administratora.\n\nDo jednej certyfikacji można przypisać tylko jedną ankietę w danym momencie.\n\nOdłączenie ankiety usuwa jej powiązanie z certyfikacją, ale odpowiedzi pracowników są zachowywane w systemie.',
          transition:
            'Samoocena nie blokuje przejścia do kolejnego etapu — jej wypełnienie jest rekomendowane, ale nie jest twardym warunkiem uruchomienia testu wiedzy.\n\nKolejny krok: Test Wiedzy (etap 2).',
        },
        test: {
          adminSteps:
            '1. Zainicjuj etap — utwórz rekord etapu testu wiedzy (nadaj nazwę, np. „Test wiedzy Q2 2026").\n\n2. Skonfiguruj test: przypisz pule pytań, ustaw liczbę pytań, próg zdawalności (np. 70%) i czas trwania.\n\n3. Utwórz terminy testów („Nowy termin") — każdy termin to konkretna data i godzina sesji. Godzina zakończenia wyliczana jest automatycznie z czasu trwania testu.\n\n4. Przypisz uczestników do etapu (po pracowniku, SAP lub kodzie stanowiska), a następnie do konkretnych terminów.\n\n5. Aktywuj etap (status Draft → Active) — dopiero wtedy pracownicy widzą test w swoim panelu. Aktywacja wymaga co najmniej jednego terminu.\n\n6. W razie potrzeby wydłuż czas pojedynczego terminu (przycisk „Wydłuż czas") dla uczestników z dodatkowymi potrzebami.',
          employeeView:
            'Pracownik widzi test w swoim panelu, gdy:\n- etap ma status „Aktywny",\n- pracownik jest przypisany do etapu i do konkretnego terminu,\n- nadszedł czas rozpoczęcia terminu.\n\nPrzed rozpoczęciem terminu pracownik widzi jedynie informację o zaplanowanej sesji (nazwa, data, godzina).\n\nPo rozpoczęciu może przystąpić do testu — ma określony czas na odpowiedzi. Po zakończeniu widzi wynik (zdany/niezdany) i punktację, jeśli konfiguracja na to pozwala.',
          purpose:
            'Test Wiedzy to formalna weryfikacja teoretycznego zrozumienia tematu certyfikacji. Pracownik odpowiada na losowo dobrane pytania z przypisanych pul i musi osiągnąć ustalony próg, aby przejść do egzaminu praktycznego.',
          rules:
            'Test jest dostępny wyłącznie w ramach zaplanowanego terminu (okno czasowe).\n\nPytania są losowane z przypisanych pul według skonfigurowanej liczby.\n\nZdanie testu wymaga osiągnięcia progu procentowego (konfigurowalnego na etapie).\n\nStatus terminu: Nowy → W toku → Ukończony. Terminy można anulować przed ich rozpoczęciem.\n\nEtap ma status: Draft (w konfiguracji), Active (dostępny dla pracowników), Completed (zakończony), Cancelled (anulowany).',
          transition:
            'Zdanie Testu Wiedzy jest warunkiem przystąpienia do Egzaminu Praktycznego (etap 3). Pracownicy z wynikiem „Nie zdał" nie są automatycznie przenoszeni dalej — decyzję o podejściu do kolejnego terminu podejmuje administrator.',
        },
      },
      stepPrefix: 'Krok',
    },
    assignSurveyDialog: {
      assignError: 'Błąd przypisywania ankiety',
      assignExistingTab: 'Przypisz istniejącą',
      assignSuccess: 'Ankieta została przypisana',
      chooseFileButton: 'Wybierz plik',
      clearDeadline: 'Wyczyść termin',
      closeButton: 'Zamknij',
      createAssignButton: 'Utwórz i przypisz',
      createError:
        'Nie udało się utworzyć ankiety. Sprawdź plik, na przykład czy numery pytań się nie powtarzają, i spróbuj ponownie.',
      createNewTab: 'Utwórz nową',
      creatingButton: 'Tworzenie...',
      currentlyAssigned: 'Aktualnie przypisana:',
      deadlineHelp: 'Opcjonalny termin dla uczestników',
      deadlineLabel: 'Termin',
      deadlinePastWarning: 'Termin jest w przeszłości',
      deadlineValidation: 'Termin musi być dzisiaj lub w przyszłości',
      detachButton: 'Odłącz',
      detachError: 'Błąd odłączania ankiety',
      detachSuccess: 'Ankieta została odłączona',
      dragDropText: 'Przeciągnij i upuść lub kliknij, aby wybrać plik .xlsx',
      fileLabel: 'Plik (XLSX) *',
      fileRequired: 'Plik jest wymagany',
      nameMaxLength: 'Nazwa może mieć maksymalnie 50 znaków',
      nameRequired: 'Nazwa jest wymagana',
      noSurveysFound:
        'Nie znaleziono ankiet do przypisania (można przypisać tylko ankiety o statusie NOWA)',
      orSelectDifferent: 'lub wybierz inną ankietę',
      searchButton: 'Szukaj',
      searchPlaceholder: 'Szukaj ankiet po nazwie...',
      surveyNameLabel: 'Nazwa ankiety *',
      title: 'Krok 1: Ankieta samooceny',
      validationErrorsTitle: 'Błędy walidacji',
      validationErrorsToast: 'Znaleziono błędy walidacji. Sprawdź szczegóły poniżej.',
    },
    deleteDialog: {
      cancelButton: 'Anuluj',
      deleteButton: 'Usuń',
      deleteError: 'Błąd usuwania certyfikatu',
      deleteSuccess: 'Certyfikat został usunięty',
      deletingButton: 'Usuwanie...',
      messageEnd: '? Tej operacji nie można cofnąć. Powiązana ankieta nie zostanie usunięta.',
      messageStart: 'Czy na pewno chcesz usunąć',
      title: 'Usuń certyfikat',
    },
    detail: {
      backButton: 'Certyfikaty',
      detachError: 'Błąd odłączania ankiety',
      detachSuccess: 'Ankieta odłączona',
      editButton: 'Edytuj',
      flowTitle: 'Przebieg certyfikacji — 4 kroki',
      label: 'Certyfikat',
      loading: 'Ładowanie certyfikatu...',
      metaCreated: 'Utworzono',
      metaDeadline: 'Termin',
      metaUpdated: 'Zaktualizowano',
      notFound: 'Nie znaleziono certyfikatu',
      participants: {
        addedAt: 'Dodano',
        attempts_few: '{{count}} podejścia',
        attempts_many: '{{count}} podejść',
        attempts_one: '{{count}} podejście',
        attempts_other: '{{count}} podejść',
        bestScore: 'Wynik {{score}}%',
        commonFilters: {
          clearAll: 'Wyczyść wszystkie',
          position: 'Stanowisko',
          positionPlaceholder: 'Wybierz stanowiska...',
          search: 'Szukaj',
          searchPlaceholder: 'Imię, nazwisko, email, SAP ID...',
          sort: 'Sortuj',
          sortOptions: {
            bestTestScoreDesc: 'Najlepszy wynik testu ↓',
            examAttemptsDesc: 'Podejść egzaminu ↓',
            lastNameAsc: 'Nazwisko ↑',
            testAttemptsDesc: 'Podejść testu ↓',
          },
          stageFiltersButton: 'Filtry etapów',
          title: 'Pracownik — filtry wspólne',
        },
        drill: {
          actions: 'Akcje',
          assigned: 'Przypisana',
          assignedMetaNo: 'przypisana: nie',
          assignedMetaYes: 'przypisana: tak',
          attempts: 'Podejścia',
          attemptsMeta_few: '{{count}} podejścia',
          attemptsMeta_many: '{{count}} podejść',
          attemptsMeta_one: '{{count}} podejście',
          attemptsMeta_other: '{{count}} podejść',
          bestScore: 'Najlepszy wynik',
          collapse: 'Zwiń',
          comment: 'Komentarz',
          comments: 'Komentarze komisji',
          commission: 'Komisja',
          commissionRole: {
            CHAIRMAN: 'Przewodniczący',
            HRBP: 'HRBP',
          },
          completedAt: 'Ukończono',
          correctOfTotal: 'Poprawne/Wszystkie',
          date: 'Data',
          daysTitle: 'Dni egzaminu',
          examTitle: 'Egzamin praktyczny (etap 3)',
          expand: 'Rozwiń',
          location: 'Miejsce',
          no: 'Nie',
          noDays: 'Brak dni egzaminu.',
          noExam: 'Brak danych egzaminu.',
          noSurvey: 'Brak danych ankiety.',
          noTerms: 'Brak terminów testu.',
          noTest: 'Brak danych testu.',
          notPassed: 'Niezdany',
          overall: 'Status ogólny',
          passed: 'Zdany',
          questions: 'pyt.',
          score: 'Wynik',
          scoreMeta: 'wynik {{score}}%',
          startedAt: 'Rozpoczęto',
          status: 'Status',
          surveyTitle: 'Ankieta (etap 1)',
          term: 'Termin',
          termsTitle: 'Terminy testu',
          testTitle: 'Test wiedzy (etap 2)',
          time: 'Czas',
          viewAnswers: 'Podgląd odpowiedzi',
          viewExam: 'Podgląd egzaminu',
          yes: 'Tak',
        },
        email: 'Email',
        error: 'Błąd ładowania uczestników',
        filters: {
          clear: 'Wyczyść',
          csvHint: 'Oddziel wartości przecinkiem lub spacją.',
          employeeSection: 'Pracownik',
          passed: 'Zdane',
          positionCodes: 'Kody stanowisk (SAP)',
          regionCodes: 'Kody regionów',
          scoreMax: 'Maks. wynik %',
          scoreMin: 'Min. wynik %',
          status: 'Status',
          surveyAssigned: 'Przypisana ankieta',
          title: 'Filtry',
          tri: {
            any: 'Dowolny',
            no: 'Nie',
            yes: 'Tak',
          },
        },
        filterStagesPlaceholder: 'Filtruj po etapie',
        flashcardDownload: {
          cancel: 'Anuluj',
          confirm: 'Pobierz',
          description:
            'Fiszka zawiera komplet wyników uczestnika. Podaj powód pobrania — trafi on do historii razem z Twoim nazwiskiem i datą.',
          reasons: {
            CERTIFICATION_PROCESS: 'Praca nad certyfikacją (omówienie wyników, komisja, odwołanie)',
            DEVELOPMENT_CONVERSATION: 'Rozmowa rozwojowa z uczestnikiem',
            HR_REQUEST: 'Prośba HR, przełożonego lub biznesu',
            OTHER: 'Inny powód',
          },
          reasonTextLabel: 'Opisz powód',
          title: 'Pobranie fiszki',
        },
        flashcardHistory: {
          empty: 'Nikt jeszcze nie pobrał fiszki tego uczestnika.',
          reasons: {
            CERTIFICATION_PROCESS: 'Praca nad certyfikacją',
            DEVELOPMENT_CONVERSATION: 'Rozmowa rozwojowa',
            HR_REQUEST: 'Prośba HR lub przełożonego',
            OTHER: 'Inny powód',
          },
          summary: 'Pobrania: {{count}}',
          title: 'Historia pobrań fiszki',
          unknownUser: 'Nieznany użytkownik',
        },
        loading: 'Ładowanie uczestników...',
        name: 'Imię i nazwisko',
        noData: 'Brak uczestników',
        noScore: 'Brak wyniku',
        openButton: 'Otwórz listę',
        practicalExamStatistics: {
          charts: {
            perAreaTitle: 'Wynik i zdawalność wg obszaru',
            perRegionTitle: 'Wynik i zdawalność wg regionu',
            perStoreTitle: 'Wynik i zdawalność wg sklepu',
          },
          download: 'Pobierz Excel',
          emptyFilters:
            'Nikt nie zdawał egzaminu przy tych filtrach. Zmień filtry, aby zobaczyć dane.',
          error: 'Nie udało się wczytać statystyk. Spróbuj ponownie.',
          kpi: {
            exams: 'Egzaminy',
            participants: 'Uczestnicy',
          },
          noData: 'Brak danych.',
          perPersonTitle: 'Wynik i zdawalność wg osoby',
          series: {
            avgScore: 'Średni wynik %',
            passRate: 'Zdawalność %',
          },
          table: {
            areaColumn: '{{area}} %',
            attemptNumber: 'Nr próby',
            chairman: 'Przewodniczący',
            duration: 'Czas trwania',
            examDate: 'Data egzaminu',
            no: 'nie',
            participant: 'Uczestnik',
            passed: 'Zdany',
            region: 'Region',
            result: 'Wynik %',
            sap: 'SAP',
            status: 'Status',
            store: 'Sklep',
            yes: 'tak',
          },
          title: 'Statystyki egzaminu praktycznego',
          tooltip: {
            exams: 'Egzaminy',
            gradedExams: 'Ocenione egzaminy',
            participants: 'Uczestnicy',
          },
        },
        rowsPerPage: 'Wierszy na stronę',
        searchPlaceholder: 'Szukaj po imieniu, nazwisku lub numerze SAP...',
        stageFilters: {
          activeCount_few: '{{count}} aktywne',
          activeCount_many: '{{count}} aktywnych',
          activeCount_one: '{{count}} aktywny',
          activeCount_other: '{{count}} aktywnych',
          all: 'Wszystkie',
          any: 'Dowolny',
          attempts: 'Podejścia',
          close: 'Zamknij',
          commission: 'Komisja (AND)',
          commissionPlaceholder: 'Wybierz członków komisji...',
          drawerTitle: 'Filtry etapów',
          exam: {
            hint: 'dzień, miejsce (OR), komisja (AND), podejścia',
            title: 'Dołącz egzamin praktyczny · etap 3',
          },
          examDay: 'Dzień egzaminu',
          loading: 'Ładowanie…',
          location: 'Miejsce (OR)',
          locationPlaceholder: 'Wybierz miejsca...',
          max: 'Max',
          min: 'Min',
          no: 'Nie',
          passed: 'Zdany',
          status: 'Status',
          survey: {
            hint: 'status, przypisanie, powiadomienie',
            title: 'Dołącz ankietę · etap 1',
          },
          surveyAssigned: 'Ankieta przypisana',
          surveyNotification: 'Powiadomienie wysłane',
          term: 'Termin',
          test: {
            hint: 'termin, wynik, podejścia',
            title: 'Dołącz test wiedzy · etap 2',
          },
          testScore: 'Wynik testu (%)',
          yes: 'Tak',
        },
        stages: 'Etapy',
        status: {
          ABSENT: 'Nieobecny',
          CANCELLED: 'Anulowany',
          COMPLETED: 'Zakończony',
          FAILED: 'Niezdany',
          IN_PROGRESS: 'W trakcie',
          NOT_STARTED: 'Nie rozpoczęty',
        },
        surveyNotAssigned: 'Nie przypisano',
        surveyStatus: {
          COMPLETED: 'Wypełniona',
          IN_PROGRESS: 'W trakcie',
          NOT_STARTED: 'Nie rozpoczęta',
        },
        table: {
          exam: 'Egzamin',
          flashcard: 'Fiszka',
          flashcardHistoryTooltip: 'Zobacz, kto pobierał fiszkę tego uczestnika',
          flashcardTooltip: 'Pobierz raport indywidualny uczestnika (PDF)',
          name: 'Uczestnik',
          position: 'Stanowisko',
          region: 'Region',
          survey: 'Ankieta',
          test: 'Test wiedzy',
        },
        teaserSubtitle: 'Wyniki ankiet, testów i egzaminów',
        testStatistics: {
          charts: {
            distributionTitle: 'Rozkład wyborów wg pytania',
            perPositionTitle: 'Średnia poprawnych odpowiedzi wg stanowiska',
            perQuestionTitle: 'Poprawne odpowiedzi wg pytania',
            perRegionTitle: 'Średnia poprawnych odpowiedzi wg regionu',
            perSectionTitle: 'Poprawne odpowiedzi wg obszaru',
          },
          detailsTitle: 'Szczegóły pytań ({{count}})',
          download: 'Pobierz Excel',
          emptyFilters:
            'Nikt nie podszedł do testu przy tych filtrach. Zmień filtry, aby zobaczyć dane.',
          error: 'Nie udało się wczytać statystyk. Spróbuj ponownie.',
          kpi: {
            executions: 'Próby',
            participants: 'Uczestnicy',
          },
          legendNoAnswer: 'Brak odpowiedzi',
          noData: 'Brak danych.',
          noValue: 'brak',
          questionShort: 'Pyt. {{number}}',
          table: {
            answerKey: 'Klucz',
            answers: 'Odp.',
            avgDuration: 'Śr. czas',
            choices: 'A / B / C / D / brak',
            content: 'Treść',
            correctRate: '% poprawnych',
            number: 'Nr',
            section: 'Obszar',
          },
          title: 'Statystyki testu wiedzy',
          tooltip: {
            attempts: 'Próby',
            avgBestScore: 'Śr. najlepszy wynik',
            avgCorrectCaption: 'średnia poprawnych',
            avgDuration: 'Śr. czas',
            correctOf: '{{correct}} / {{answered}} poprawnych',
            noAnswer: 'brak',
            noAttempts: 'Brak prób.',
            noSection: 'Bez obszaru',
            participants: 'Uczestnicy',
            question: 'Pytanie {{number}}',
            questionsInSection: 'Pytania w obszarze',
            seconds: 's',
          },
        },
        title: 'Uczestnicy',
        titleTooltip:
          'Znajdziesz tu wszystkich uczestników certyfikacji. Uczestnik pojawi się na liście, gdy zostanie przypisany do co najmniej jednego etapu. Dodawanie i usuwanie uczestników możliwe jest wyłącznie z poziomu konfiguracji poszczególnych etapów.',
      },
      stageStatusChange: {
        activateButton: 'Aktywuj',
        activateSuccess: 'Etap aktywowany',
        cancelButton: 'Anuluj',
        cancelSuccess: 'Etap anulowany',
        completeButton: 'Zakończ',
        completeSuccess: 'Etap ukończony',
        error: 'Błąd zmiany statusu etapu',
        label: 'Zmień status',
        revertButton: 'Wznów',
        revertSuccess: 'Etap wznowiony',
        testClosureConfirm: {
          cancelAction: 'Anuluj',
          confirmAction: 'Tak, zamknij',
          confirmCancel:
            'Zamykasz cały test. Wszystkie terminy w ramach tego testu zostaną anulowane. Czy chcesz kontynuować?',
          confirmComplete:
            'Zamykasz cały test. Wszystkie terminy w ramach tego testu zostaną zamknięte (terminy w trakcie zostaną oznaczone jako ukończone, jeszcze nierozpoczęte – jako anulowane). Czy chcesz kontynuować?',
          titleCancel: 'Anulowanie testu',
          titleComplete: 'Zamknięcie testu',
        },
      },
      statusChange: {
        activateButton: 'Aktywuj',
        activateSuccess: 'Certyfikat aktywowany',
        cancelButton: 'Anuluj',
        cancelSuccess: 'Certyfikat anulowany',
        completeButton: 'Zakończ',
        completeSuccess: 'Certyfikat ukończony',
        error: 'Błąd zmiany statusu',
        label: 'Zmień status',
      },
      statusWarningsDialog: {
        activateButton: 'Aktywuj',
        closeButton: 'Rozumiem',
        subtitle: 'Akcja zakończona pomyślnie. Zapoznaj się z poniższymi informacjami:',
        title: 'Zakończono z ostrzeżeniami',
      },
      steps: {
        developmentCenter: {
          description: 'Wyniki sesji Development Center i raporty uczestników',
          initializeButton: 'Uruchom etap Development Center',
          manageHint:
            'Wgrywanie arkuszy, udostępnianie raportów i lista osób znajdują się na ekranie etapu.',
          openStageButton: 'Przejdź do etapu',
          optionalHint:
            'Etap jest opcjonalny — jeśli certyfikacja nie obejmuje Development Center, po prostu go nie uruchamiaj.',
          reportsNotShared: 'Raporty nie są jeszcze udostępnione — brak daty udostępnienia.',
          reportsScheduledFrom: 'Udostępnienie raportów zaplanowane na {{date}}.',
          reportsSharedSince: 'Raporty udostępnione uczestnikom od {{date}}.',
          resultsSummary: 'Wyniki ogólne: {{withResults}} z {{total}} osób.',
          stageActive: 'Etap Development Center jest uruchomiony',
          stageCreatedError: 'Nie udało się uruchomić etapu Development Center',
          stageCreatedSuccess: 'Etap Development Center został uruchomiony',
          stageNotInitialized: 'Ta certyfikacja nie obejmuje jeszcze Development Center',
          title: 'Development Center',
        },
        developmentPlan: {
          campaignNoName: '(kampania bez nazwy)',
          campaignStatus: {
            [DevelopmentCycleCampaignStatusDto.Cancelled]: 'Anulowana',
            [DevelopmentCycleCampaignStatusDto.Draft]: 'Szkic',
            [DevelopmentCycleCampaignStatusDto.Finished]: 'Zakończona',
            [DevelopmentCycleCampaignStatusDto.InProgress]: 'W trakcie',
          },
          coveredLabel: 'Objętych',
          description:
            'Kampania cyklu rozwojowego, w której uczestnicy tej certyfikacji zakładają plany rozwoju',
          directionLabel: 'Kierunek rozwoju',
          directionMissing: 'Brak',
          loadError: 'Nie udało się wczytać etapu',
          loadErrorHint: 'Spróbuj ponownie za chwilę.',
          noPermission: 'Nie masz dostępu do kampanii cyklu rozwojowego',
          notStarted: 'Etap nie został wystartowany',
          notStartedHint:
            'Plany rozwojowe zaczynają się od założenia kampanii dla tej certyfikacji.',
          notStartedNoPermissionHint:
            'Poproś o założenie kampanii osobę, która zarządza kampaniami cyklu rozwojowego.',
          openCampaignsButton: 'Przejdź do kampanii',
          openMembersButton: 'Objęci pracownicy',
          retryButton: 'Spróbuj ponownie',
          tasksStartLabel: 'Start zadań',
          title: 'Plan rozwoju',
          windowLabel: 'Okno cyklu',
        },
        knowledgeTest: {
          attachButton: 'Przypisz istniejącą',
          attachDialog: {
            attachButton: 'Przypisz',
            attachError: 'Błąd przypisywania puli',
            attachingButton: 'Przypisywanie...',
            attachSuccess: 'Pula przypisana pomyślnie',
            cancelButton: 'Anuluj',
            loading: 'Ładowanie pul...',
            noPools: 'Nie znaleziono pul pytań',
            questionCount: 'pytań',
            searchPlaceholder: 'Szukaj pul...',
            title: 'Przypisz istniejącą pulę',
          },
          browseButton: 'Przeglądaj pytania',
          config: {
            backNav: 'powrót',
            notConfigured: 'Test nie został jeszcze skonfigurowany',
            pass: 'zdane',
            questions: 'pytania',
          },
          description:
            'Formalny test weryfikacji wiedzy oceniający teoretyczne rozumienie tematu certyfikacji przez uczestnika. Skonfiguruj pule pytań, zaplanuj terminy testów i zarządzaj uczestnikami, aby przeprowadzić egzamin.',
          detachButton: 'Odłącz pulę',
          detachError: 'Błąd odłączania puli',
          detachSuccess: 'Pula odłączona',
          emptyPools: 'Brak przypisanych pul pytań',
          initializeButton: 'Inicjuj',
          manageParticipantsAndTerms: 'Zarządzaj uczestnikami i terminami',
          openConfig: 'Otwórz konfigurację',
          poolsTitle: 'Pule pytań',
          stageCreatedError: 'Błąd tworzenia etapu testu',
          stageCreatedSuccess: 'Etap testu został utworzony',
          stageNamePlaceholder: 'Nazwa etapu',
          stageNotInitialized: 'Etap testu nie został jeszcze zainicjowany',
          stageParticipants: {
            addButton: 'Dodaj uczestników',
            addByEmployee: 'Według pracownika',
            addByPosition: 'Według kodu stanowiska',
            addBySap: 'Według numeru SAP',
            addedHeader: 'Dodano',
            addError: 'Błąd dodawania uczestników',
            addPositionButton: 'Dodaj {{count}} kod(y) stanowisk',
            addPositionCodesHelper:
              'Wprowadź kody stanowisk (zaczynające się od 2 lub 7) oddzielone przecinkami lub nową linią',
            addPositionError: 'Błąd dodawania według stanowiska',
            addPositionInvalidFormat:
              'Nieprawidłowy format (wymagane 8 cyfr zaczynających się od 2 lub 7): {{codes}}',
            addPositionPlaceholder: 'np. 20000935, 70148208',
            addSapButton: 'Dodaj {{count}} numer(y) SAP',
            addSapError: 'Błąd dodawania według numeru SAP',
            addSapHelper: 'Wprowadź numery SAP oddzielone przecinkami lub nową linią',
            addSapInvalidFormat: 'Nieprawidłowy format (wymagane 8 cyfr): {{numbers}}',
            addSapPlaceholder: 'np. 12345678, 87654321',
            addSelectedButton: 'Dodaj {{count}} uczestnika(-ów)',
            addSuccess: 'Uczestnicy dodani do etapu',
            allInStageEmpty: 'Wszyscy uczestnicy certyfikacji są już w tym etapie',
            emailHeader: 'Email',
            filtersClear: 'Wyczyść',
            filterStatus: 'Status terminu',
            filtersToggle: 'Filtry',
            filterTerms: 'Filtruj po terminie',
            more: 'więcej',
            nameHeader: 'Uczestnik',
            noParticipants: 'Brak uczestników',
            noParticipantsHint: 'Dodaj pracowników z puli uczestników certyfikacji powyżej',
            noTerms: 'Brak terminów',
            removeError: 'Błąd usuwania uczestnika',
            removeSuccess: 'Uczestnik usunięty z etapu',
            removeTooltip: 'Usuń z etapu',
            searchParticipantsPlaceholder: 'Szukaj uczestników certyfikacji…',
            searchPlaceholder: 'Szukaj po nazwie lub emailu…',
            showing: '{{count}} z {{total}} uczestników',
            showLess: 'pokaż mniej',
            status: {
              ASSIGNED: 'Przypisany',
              EXPIRED: 'Wygasły',
              FAILED: 'Nie zdał',
              PASSED: 'Zdał',
              STARTED: 'W trakcie',
            },
            termHistoryHeader: 'Historia terminów',
            title: 'Uczestnicy etapu',
          },
          testTerms: {
            cancelButton: 'Anuluj',
            cancelledLabel: 'Anulowany',
            completeButton: 'Zakończ',
            completedLabel: 'Ukończony',
            createDialog: {
              cancelButton: 'Anuluj',
              createButton: 'Utwórz termin',
              createError: 'Błąd tworzenia terminu',
              createSuccess: 'Termin testowy utworzony',
              creatingButton: 'Tworzenie…',
              endHint:
                'Wyliczana automatycznie na podstawie czasu trwania testu skonfigurowanego dla tego etapu',
              endLabel: 'Data i czas zakończenia (wyliczana)',
              nameLabel: 'Nazwa terminu *',
              nameMaxLength: 'Nazwa może mieć maksymalnie 50 znaków',
              namePlaceholder: 'np. Test wiedzy czerwiec 2025',
              nameRequired: 'Nazwa terminu jest wymagana',
              startLabel: 'Data i czas rozpoczęcia *',
              startRequired: 'Podaj datę i godzinę rozpoczęcia',
              subtitle: 'Zaplanuj nową sesję testową dla tego etapu',
              title: 'Nowy termin',
            },
            createFirstButton: 'Utwórz pierwszy termin',
            editDialog: {
              cancelButton: 'Anuluj',
              endLabel: 'Data i godzina zakończenia',
              hint: 'Czas rozpoczęcia i zakończenia jest opcjonalny.',
              nameLabel: 'Nazwa terminu *',
              namePlaceholder: 'np. Test wiedzy czerwiec 2025',
              nameRequired: 'Nazwa terminu jest wymagana',
              saveButton: 'Zapisz zmiany',
              savingButton: 'Zapisywanie…',
              startLabel: 'Data i godzina rozpoczęcia',
              startLockedHelper:
                'Czas rozpoczęcia można zmienić tylko dla terminów ze statusem „Nowy".',
              subtitle: 'Zaktualizuj nazwę i harmonogram dla tego terminu testu',
              title: 'Edytuj termin testu',
              updateError: 'Nie udało się zaktualizować terminu testu',
              updateSuccess: 'Termin testu zaktualizowany',
            },
            extendTimeButton: 'Wydłuż czas',
            extendTimeConfirm: 'Wydłuż',
            extendTimeError: 'Nie udało się wydłużyć czasu terminu',
            extendTimeHint: 'Maksymalne dozwolone wydłużenie: {{max}} min',
            extendTimeLabel: 'Liczba minut',
            extendTimeSuccess: 'Czas terminu został wydłużony',
            extendTimeTitle: 'Wydłuż czas terminu',
            hideParticipants: 'Ukryj uczestników',
            inProgressLabel: 'W toku',
            newButton: 'Nowy termin',
            newLabel: 'Nowy',
            noTermsDescription: 'Utwórz termin, aby zaplanować i zarządzać sesjami testowymi',
            noTermsTitle: 'Brak terminów testowych',
            participants: 'uczestników',
            participantsDialog: {
              activityReportError: 'Nie udało się pobrać raportu aktywności',
              activityReportTooltip: 'Pobierz raport aktywności (PDF)',
              addButton: 'Dodaj',
              addError: 'Błąd dodawania uczestników',
              addSelectedButton: 'Dodaj {{count}} uczestnika(-ów)',
              addSuccess: 'Dodano {{count}} uczestnika(-ów) do terminu',
              allInTermEmpty: 'Wszyscy uczestnicy etapu są już w tym terminie',
              closeButton: 'Zamknij',
              emailHeader: 'Email',
              noParticipants: 'Brak uczestników',
              noParticipantsHint: 'Dodaj uczestników z puli etapu powyżej',
              participantHeader: 'Uczestnik',
              removeError: 'Błąd usuwania uczestnika',
              removeSuccess: 'Uczestnik usunięty z terminu',
              removeTooltip: 'Usuń z terminu',
              searchByNameEmail: 'Szukaj po nazwie lub emailu…',
              searchPlaceholder: 'Szukaj uczestników etapu…',
              selectLabel: 'Wybierz z uczestników etapu',
              statusAssigned: 'Przypisany',
              statusExpired: 'Wygasły',
              statusFailed: 'Nie zdany',
              statusHeader: 'Status',
              statusPassed: 'Zdany',
              statusStarted: 'Rozpoczęty',
              subtitle: 'Uczestnicy terminu',
              title: 'Zarządzanie uczestnikami terminu',
              viewResultsTooltip: 'Zobacz wyniki',
            },
            resultsDialog: {
              closeButton: 'Zamknij',
              correctAnswerHeader: 'Poprawna odpowiedź',
              correctLabel: 'Poprawna',
              executionStatus: {
                COMPLETED_FAILED: 'Niezdany',
                COMPLETED_PASSED: 'Zdany',
                EXPIRED: 'Wygasły',
                IN_PROGRESS: 'W trakcie',
                NOT_STARTED: 'Nie rozpoczęty',
              },
              filterAll: 'Wszystkie',
              filterCorrect: 'Zdane',
              filterIncorrect: 'Niezdane',
              incorrectLabel: 'Niepoprawna',
              noAnswerLabel: 'Bez odpowiedzi',
              questionHeader: 'Pytanie',
              sectionHeader: 'Obszar',
              unansweredLabel: '—',
              yourAnswerHeader: 'Podana odpowiedź',
            },
            resultsListDialog: {
              closeButton: 'Zamknij',
              correctHeader: 'Poprawne',
              noResults: 'Brak wyników',
              noResultsHint: 'Wyniki pojawią się po zakończeniu testu przez uczestników',
              participantHeader: 'Uczestnik',
              scoreHeader: 'Wynik',
              searchPlaceholder: 'Szukaj po nazwie…',
              statusHeader: 'Status',
              subtitle: '{{count}} uczestników',
              title: 'Wyniki terminu',
              viewDetailTooltip: 'Zobacz odpowiedzi',
            },
            revertButton: 'Wznów',
            revertSuccess: 'Termin wznowiony',
            showParticipants: 'Uczestnicy ({{count}})',
            startBlockedReason:
              'Przypisz co najmniej jednego uczestnika do tego terminu przed uruchomieniem',
            startBlockedStageInactiveReason: 'Nie można rozpocząć terminu — etap nie jest aktywny',
            startButton: 'Rozpocznij',
            startCertificationDraftInfo:
              'Certyfikacja jest w statusie Szkic. Po potwierdzeniu zostanie automatycznie aktywowana, aby uczestnicy mieli dostęp.',
            startExtensionInfo:
              'Wydłużenie czasu jest możliwe tylko w trakcie trwania testu. Po upływie czasu terminu nie można już przedłużyć.',
            startInFutureInfo:
              'Egzamin zostanie rozpoczęty, ale uczestnicy będą mogli go zacząć dopiero od {{date}}.',
            startInPastInfo:
              'Zaplanowany termin rozpoczęcia ({{date}}) już minął. Egzamin rozpocznie się teraz.',
            startStageDraftInfo:
              "Etap '{{stageName}}' jest w statusie Szkic. Po potwierdzeniu zostanie automatycznie aktywowany, aby uczestnicy mieli dostęp do testu.",
            title: 'Terminy testów',
            updatedAt: 'Zaktualizowano',
            updateError: 'Błąd aktualizacji statusu terminu',
            updateSuccess: 'Status terminu zaktualizowany',
            viewParticipantsTooltip: 'Zobacz uczestników',
            viewResultsTooltip: 'Zobacz wyniki',
          },
          title: 'Test wiedzy',
          uploadButton: 'Prześlij pulę',
          uploadToPoolButton: 'Prześlij do puli',
        },
        practicalTest: {
          comingSoon: 'Wkrótce',
          config: { notConfigured: 'Egzamin nie został jeszcze skonfigurowany' },
          description: 'Opis kroku',
          examDays: 'Dni egzaminacyjne',
          initializeButton: 'Inicjalizuj',
          openConfig: 'Otwórz konfigurację',
          stageCreatedError: 'Błąd tworzenia etapu egzaminu',
          stageCreatedSuccess: 'Etap egzaminu został utworzony',
          stageNamePlaceholder: 'Nazwa etapu...',
          stageNotInitialized: 'Etap testu praktycznego nie został zainicjalizowany.',
          title: 'Test praktyczny',
        },
        selfAssessment: {
          description:
            'Ustrukturyzowana ankieta samooceny wypełniana przez uczestnika przed rozpoczęciem procesu certyfikacji. Pomaga ustalić poziom wyjściowy i zidentyfikować luki rozwojowe.',
          title: 'Samoocena',
        },
      },
    },
    developmentCenterStage: {
      backButton: 'Wróć do certyfikacji',
      card: { edit: 'Zmień', hide: 'Ukryj' },
      certificationsTitle: 'Certyfikacje',
      coverage: { percent: '{{percent}}%', ratio: '{{covered}} / {{total}}' },
      expectedScore: {
        error: 'Nie udało się zapisać oczekiwanego wyniku. Spróbuj ponownie.',
        fieldLabel: 'Oczekiwany wynik',
        hint: 'Punkt odniesienia, do którego porównywane są wyniki wszystkich uczestników etapu. Zmiana tutaj dotyczy również wyników już wgranych.',
        label: 'Oczekiwany wynik',
        notSet: 'nie ustawiono',
        placeholder: 'Wybierz wynik',
        save: 'Zapisz wynik',
        saved: 'Oczekiwany wynik został zapisany.',
        scaleUnavailable: 'Brak skali',
        valueWithScale: '{{score}} w skali {{min}}-{{max}}',
      },
      importSummary: {
        allCovered: 'Każdy uczestnik etapu ma raport indywidualny.',
        assigned: 'nowych raportów',
        close: 'Gotowe',
        copied: 'Skopiowano',
        copyFailed:
          'Przeglądarka zablokowała dostęp do schowka. Zaznacz listę i skopiuj ją ręcznie.',
        copyList: 'Kopiuj listę',
        missing: 'bez raportu',
        missingHint: 'Te osoby ({{count}}) nadal nie mają raportu indywidualnego:',
        rejected: 'odrzuconych wpisów',
        rejectedHint:
          'Część plików z paczki nie została przyjęta. Otwórz szczegóły, aby zobaczyć powód dla każdego z nich.',
        showErrors: 'Zobacz odrzucone pliki',
        subtitle: 'Co paczka wniosła do etapu',
        title: 'Podsumowanie importu',
        unknownPerson: 'Osoba bez nazwiska',
      },
      label: 'Etap Development Center',
      lastImport: {
        empty: 'Nie wgrano jeszcze żadnego arkusza.',
        label: 'Ostatni import',
        meta: '{{file}} · {{date}} · {{author}}',
        problems: 'Import zgłosił problemy, które nadal czekają na rozwiązanie.',
        resolve: 'Rozwiąż błędy',
        success: 'Import zakończył się bez uwag · zapisanych uczestników: {{count}}',
        unknownAuthor: 'nieznany autor',
        unknownFile: 'Plik bez nazwy',
      },
      loading: 'Wczytywanie etapu…',
      notFound: 'Nie znaleziono certyfikacji',
      notInitialized: 'Ta certyfikacja nie obejmuje jeszcze Development Center',
      participants: {
        columns: {
          feedback: 'Feedback DC',
          name: 'Uczestnik',
          position: 'Stanowisko',
          region: 'Region',
          removeFromStage: 'Wypisz',
          report: 'Raport',
          results: 'Wyniki ogólne',
          sapNumber: 'Numer SAP',
        },
        downloadReport: 'Pobierz raport indywidualny',
        empty: 'Brak osób spełniających wybrane filtry.',
        emptyStage: 'W etapie nie ma jeszcze żadnych osób.',
        enrolAll: {
          action: 'Dodaj uczestników certyfikacji',
          cancel: 'Anuluj',
          confirm: 'Dodaj',
          description:
            'Do etapu zostaną dopisani wszyscy uczestnicy certyfikacji, których jeszcze w nim nie ma. Osoby już zapisane zostaną pominięte.',
          result: 'Dodano: {{added}}. Pominięto już zapisanych: {{skipped}}.',
          title: 'Dodać uczestników certyfikacji do etapu?',
        },
        feedback: {
          clearConfirm: {
            cancel: 'Anuluj',
            confirm: 'Cofnij',
            description:
              'Osoba wraca do oczekiwania na spotkanie feedbackowe, a zadanie przygotowania planu zostanie jej anulowane.',
            title: 'Cofnąć spotkanie feedbackowe?',
          },
          coverageEmpty: 'Nikt w etapie nie ma jeszcze spotkania feedbackowego.',
          coverageStatus: '{{missing}} osób czeka jeszcze na spotkanie feedbackowe.',
          coverageTitle: 'Spotkania feedbackowe po sesji',
          source: { IMPORT: 'z pliku', MANUAL: 'ustawione ręcznie' },
          statusCompleted: 'Spotkanie odbyte',
          statusMissing: 'Brak spotkania',
          toggleAriaLabel: 'Spotkanie feedbackowe odbyte',
          tooltipAuthor: 'Ustawił: {{author}}',
          tooltipDate: 'Kiedy: {{date}}',
          tooltipSource: 'Źródło: {{source}}',
          unknownAuthor: 'nieznany',
        },
        feedbackImport: {
          acceptedFormats: '(akceptowane formaty: .xls, .xlsx)',
          action: 'Wgraj plik feedbacku',
          cancel: 'Anuluj',
          columnsHint:
            'Dwie kolumny: „nr sap" i „feedback". W kolumnie feedback wpisz TAK lub NIE. Pusta komórka zostawia dotychczasowe ustawienie bez zmian.',
          columnsTitle: 'Co ma zawierać plik',
          dropzoneLabel: 'Przeciągnij i upuść plik tutaj, lub kliknij aby wybrać',
          form: { resetButton: 'Usuń', uploadButton: 'Wybierz plik' },
          submit: 'Wgraj plik',
          subtitle: 'Oznacz hurtem, kto ma za sobą spotkanie feedbackowe',
          summary: {
            allSaved: 'Wszystkie wiersze z pliku zostały zapisane.',
            close: 'Gotowe',
            problemRow: 'Wiersz {{row}} · SAP {{sap}}',
            problemRowWithoutSap: 'Wiersz {{row}}',
            problems: 'bez zapisu',
            problemsHint: 'Tych wierszy nie zapisano. Pozostałe zostały zapisane:',
            rowsRead: 'przeczytanych wierszy',
            setNo: 'zdjęto spotkanie',
            setYes: 'oznaczono spotkanie',
            skipped: 'pominiętych',
            subtitle: 'Co plik zmienił w etapie',
            title: 'Podsumowanie importu feedbacku',
            unchanged: 'bez zmian',
          },
          title: 'Wgraj plik feedbacku',
          uploadError: 'Nie udało się odczytać pliku. Sprawdź kolumny i spróbuj ponownie.',
        },
        filters: {
          ALL: 'Wszyscy',
          label: 'Braki',
          WITHOUT_FEEDBACK: 'Bez feedbacku DC',
          WITHOUT_REPORT: 'Bez raportu',
          WITHOUT_RESULTS: 'Bez wyników',
        },
        foundCount_few: 'Znaleziono {{count}} osoby',
        foundCount_many: 'Znaleziono {{count}} osób',
        foundCount_one: 'Znaleziono {{count}} osobę',
        foundCount_other: 'Znaleziono {{count}} osób',
        notAssignedHint:
          'Ta osoba pojawiła się wyłącznie w arkuszu Development Center — nie jest uczestnikiem pozostałych etapów certyfikacji.',
        removeFromStage: {
          action: 'Wypisz z etapu',
          cancel: 'Anuluj',
          confirm: 'Wypisz',
          description:
            'Osoba zniknie z listy etapu i przestanie być liczona do braków. Wgrany wynik i raport zostają nietknięte.',
          title: 'Wypisać osobę z etapu?',
        },
        searchPlaceholder: 'Szukaj po imieniu, nazwisku lub numerze SAP',
        statusMissing: 'Brak',
        statusPresent: 'Wgrane',
        totalPill: 'osób {{total}}',
        unknownPerson: 'Osoba bez nazwiska',
        withReportPill: 'z raportem {{count}}',
        withResultsPill: 'z wynikami {{count}}',
      },
      reports: {
        acceptedFormats: '(akceptowany format: .zip)',
        coverageTitle: 'Raporty indywidualne',
        dropzoneLabel: 'Przeciągnij i upuść paczkę tutaj, lub kliknij aby wybrać plik',
        form: { resetButton: 'Usuń', uploadButton: 'Wybierz plik' },
        overwriteWarning:
          'Nowa paczka zastępuje raporty już wgrane do etapu. Osoby, których raportu nie ma w nowym archiwum, stracą dostęp do poprzedniego.',
        overwriteWarningTitle: 'Nowa paczka nadpisuje obecne raporty',
        replaceLabel: 'Paczka z raportami',
        showWithoutReport: 'Pokaż braki',
        statusComplete: 'Każda osoba w etapie ma raport indywidualny.',
        statusEmpty:
          'Nie wgrano jeszcze paczki z raportami. Wgraj archiwum ZIP, aby przypisać raporty PDF do osób.',
        statusNone: 'Brak daty, raporty niedostępne',
        statusPartial: '{{missing}} osób czeka jeszcze na swój raport.',
        statusScheduled: 'Udostępnienie zaplanowane na {{date}}',
        statusShared: 'Raporty udostępnione od {{date}}',
        transferProgress: 'Wysyłanie paczki… {{percent}}%',
        unpackingLabel: 'Rozpakowywanie paczki',
        unpackingProgress: 'Rozpakowywanie… {{percent}}%',
        unpackingStarted:
          'Paczka została wysłana i jest rozpakowywana. Po zakończeniu zobaczysz podsumowanie przypisanych raportów.',
        uploadButton: 'Wgraj paczkę',
        uploadError: 'Nie udało się wgrać paczki z raportami',
        uploadHint:
          'Archiwum ZIP z raportami PDF uczestników. Do nadejścia daty udostępnienia nikt nie zobaczy swojego raportu.',
        uploadLabel: 'Wgraj paczkę z raportami',
        visibilityLabel: 'Udostępnienie raportów',
        visibleFromClear: 'Cofnij udostępnienie',
        visibleFromCleared: 'Data została usunięta, uczestnicy nie pobiorą już swoich raportów.',
        visibleFromError: 'Nie udało się zapisać daty udostępnienia. Spróbuj ponownie.',
        visibleFromField: 'Raporty widoczne od',
        visibleFromHint:
          'Jedna data dla całego etapu. Przed nią uczestnik widzi etap i zapowiedzianą datę, ale nie pobierze pliku. Zmiana daty po jej minięciu cofa udostępnienie.',
        visibleFromSave: 'Zapisz datę',
        visibleFromSaved: 'Data udostępnienia została zapisana.',
      },
      results: {
        acceptedFormats: '(akceptowane formaty: .xls, .xlsx)',
        coverageTitle: 'Wyniki ogólne',
        downloadFile: 'Pobierz wgrany plik',
        dropzoneLabel: 'Przeciągnij i upuść arkusz tutaj, lub kliknij aby wybrać plik',
        expectedScoreLabel: 'Oczekiwany wynik',
        expectedScoreMeta: 'Oczekiwany wynik',
        expectedScorePlaceholder: 'Wybierz oczekiwany wynik',
        expectedScoreValueWithScale: '{{score}} w skali {{min}}-{{max}}',
        form: { resetButton: 'Usuń', uploadButton: 'Wybierz plik' },
        historyLabel: 'Historia wgrań',
        importMeta: '{{date}} · {{author}} · zapisanych uczestników: {{count}}',
        replaceLabel: 'Arkusz z wynikami',
        scaleUnavailable: 'Zakres skali pochodzi z kontraktu pliku i nie jest jeszcze dostępny.',
        showWithoutResults: 'Pokaż braki',
        statusComplete: 'Każda osoba w etapie ma wgrane wyniki ogólne.',
        statusEmpty:
          'Nie wgrano jeszcze arkusza z wynikami. Wgraj plik, aby wprowadzić wyniki sesji.',
        statusNoneCovered:
          'Plik został wgrany, ale żaden uczestnik nie ma jeszcze z niego wyników.',
        statusPartial: '{{missing}} osób czeka jeszcze na swoje wyniki.',
        unknownAuthor: 'nieznany autor',
        unknownFile: 'Plik bez nazwy',
        uploadButton: 'Wgraj wyniki',
        uploadError: 'Nie udało się rozpocząć importu wyników',
        uploadHint:
          'Jeden arkusz od dostawcy z wynikami sesji. Wybrany tutaj oczekiwany wynik jest punktem odniesienia dla ocen wszystkich uczestników z pliku.',
        uploadLabel: 'Wgraj arkusz z wynikami',
        uploadStarted:
          'Import wyników został rozpoczęty. Po zakończeniu zobaczysz podsumowanie i ewentualne uwagi.',
      },
      subtitle: 'Wyniki sesji i raporty uczestników',
      tabs: {
        participants: 'Osoby w etapie',
        reports: 'Raporty indywidualne',
        results: 'Wyniki ogólne',
      },
      title: 'Development Center',
      upload: { collapse: 'Ukryj formularz', expand: 'Wgraj nowy plik' },
    },
    examStageConfig: {
      bar: {
        attemptsUnit: 'podejść',
        passPrefix: 'Próg',
        questionsUnit: 'pyt.',
      },
      cancelButton: 'Anuluj',
      configureButton: 'Skonfiguruj egzamin',
      confirmDialog: {
        cancel: 'Wróć do edycji',
        confirm: 'Potwierdź i zapisz',
        title: 'Potwierdzenie konfiguracji',
      },
      editButton: 'Edytuj konfigurację',
      fields: {
        maxAttempts: 'Maks. prób',
        maxQuestions: 'Maks. pytań',
        minQuestions: 'Min. pytań',
        noLimit: 'Bez limitu',
        ofQuestions: 'pytań',
        questionsCount: 'Liczba pytań',
        questionsShort: 'pyt.',
        requiredToPass: 'Wymagana l. pytań do zdania',
        timeLimitMinutes: 'Limit czasu (min)',
      },
      notConfigured: 'Egzamin nie został jeszcze skonfigurowany',
      saveButton: 'Zapisz konfigurację',
      validation: {
        maxAttempts: 'Musi być między 1 a 10',
        maxQuestions: 'Musi być ≥ min. pytań',
        minQuestions: 'Musi być ≥ 1',
        questionsCount: 'Musi być ≥ 1',
        requiredToPass: 'Musi być między {{min}} a {{max}}',
      },
    },
    examStageDetail: {
      commissionMemberSearch: {
        extraMemberBadge: 'Spoza struktury',
        loading: 'Wczytywanie…',
        noOptions: 'Nie znaleziono osób, które można wybrać do komisji.',
      },
      createExamDay: {
        cancelButton: 'Anuluj',
        createButton: 'Utwórz',
        fields: {
          chairman: 'Przewodniczący',
          date: 'Data',
          hrbp: 'HRBP',
          location: 'Miejsce',
        },
        title: 'Utwórz dzień egzaminacyjny',
      },
      examDayDetail: {
        addParticipant: 'Dodaj uczestnika',
        addParticipantDialog: {
          addedEmpty: 'Brak dodanych uczestników',
          addedTitle: 'Dodani',
          addTooltip: 'Dodaj do dnia egzaminacyjnego',
          allRegions: 'Wszystkie regiony',
          availableTitle: 'Dostępni',
          clearFilters: 'Wyczyść filtry',
          close: 'Zamknij',
          empty: 'Wszyscy uczestnicy są już przypisani',
          noResults: 'Brak wyników dla wybranych filtrów',
          regionLabel: 'Region',
          searchPlaceholder: 'Szukaj po imieniu, nazwisku lub nr SAP...',
          title: 'Dodaj uczestników do dnia egzaminacyjnego',
        },
        cancelDay: 'Anuluj',
        cancelExam: 'Anuluj egzamin',
        chairman: 'Przewodniczący',
        columns: {
          actions: 'Akcje',
          examStatus: 'Status egzaminu',
          name: 'Imię i nazwisko',
          result: 'Wynik',
        },
        completeDay: 'Zakończ',
        dayStatus: {
          [ExamDayStatusDto.Cancelled]: 'Anulowany',
          [ExamDayStatusDto.Completed]: 'Zakończony',
          [ExamDayStatusDto.Draft]: 'Nowy',
          [ExamDayStatusDto.InProgress]: 'W trakcie',
        },
        examsTitle: 'Egzaminy',
        generateExams: 'Generuj egzaminy',
        noExam: 'Brak egzaminu',
        noParticipants: 'Brak przypisanych uczestników do tego dnia',
        participants: 'Uczestnicy',
        removeParticipant: 'Usuń uczestnika',
        startDay: 'Rozpocznij dzień',
      },
      examDays: {
        addButton: 'Dodaj dzień egzaminacyjny',
        backToList: 'Wróć do listy',
        cancelButton: 'Anuluj',
        chairman: 'Przewodniczący',
        completeButton: 'Zakończ',
        dayStatus: {
          [ExamDayStatusDto.Cancelled]: 'Anulowany',
          [ExamDayStatusDto.Completed]: 'Zakończony',
          [ExamDayStatusDto.Draft]: 'Nowy',
          [ExamDayStatusDto.InProgress]: 'W trakcie',
        },
        empty: 'Nie utworzono jeszcze żadnych dni egzaminacyjnych',
        filters: {
          clear: 'Wyczyść',
          commissionMember: 'Komisja',
          dateFrom: 'Data od',
          dateTo: 'Data do',
          noResults: 'Brak wyników dla wybranych filtrów',
          searchPlaceholder: 'Szukaj po nazwie lub członku komisji...',
          toggle: 'Filtry',
        },
        participantsCount: 'uczestników',
        startBlockedStageInactiveReason: 'Nie można rozpocząć dnia — etap nie jest aktywny',
        startButton: 'Rozpocznij',
        title: 'Dni egzaminacyjne',
        updateError: 'Błąd zmiany statusu dnia egzaminacyjnego',
      },
      examQuestion: {
        answerKeyLabel: 'Klucz odpowiedzi',
        chairmanComment: 'Komentarz przewodniczącego',
        commentPlaceholder: 'Dodaj komentarz...',
        hrbpComment: 'Komentarz HRBP',
        questionLabel: 'Pytanie',
        reportAreaLabel: 'Obszar',
        reportLabel: 'Raport',
        reportShareNotSharedLabel: 'Raport nie został udostępniony egzaminowanemu',
        reportShareShareButton: 'Udostępnij raport egzaminowanemu',
        reportShareSharedLabel: 'Raport udostępniony egzaminowanemu',
        reportShareUnshareButton: 'Cofnij udostępnienie',
        reportTag: 'Raport',
        subquestionLabel: 'Podpytanie',
        subquestionsTitle: 'Podpytania',
        verdictFail: 'Niezdane',
        verdictPass: 'Zdane',
        verdictRejected: 'Odrzucone',
      },
      extraCommissionMembers: {
        addButton: 'Dodaj osobę',
        addedBy: '{{date}}, {{name}}',
        columns: {
          actions: 'Akcje',
          added: 'Dodano',
          name: 'Imię i nazwisko',
          roles: 'Funkcja w komisji',
          sapNumber: 'Nr SAP',
        },
        description:
          'Osoby spoza struktury przełożonych, które można wybrać do komisji dnia egzaminacyjnego. Osoba z listy dostaje dostęp do panelu i do dni egzaminacyjnych, w których jest w komisji.',
        dialog: {
          add: 'Dodaj',
          addTitle: 'Dodaj osobę do komisji',
          alreadyOnList: 'Już na liście',
          cancel: 'Anuluj',
          editTitle: 'Zmień funkcje w komisji',
          loading: 'Wczytywanie…',
          noOptions: 'Brak osób pasujących do wyszukiwania.',
          personLabel: 'Osoba',
          rolesLabel: 'Może być wybrana jako',
          rolesRequired: 'Zaznacz co najmniej jedną funkcję.',
          save: 'Zapisz',
          saveError: 'Nie udało się zapisać zmian. Spróbuj ponownie.',
          searchPlaceholder: 'Szukaj po imieniu, nazwisku, e-mailu lub nr SAP',
        },
        editTooltip: 'Zmień funkcje w komisji',
        empty:
          'Na liście nie ma jeszcze nikogo. Dodaj osobę, jeśli do komisji ma wejść ktoś spoza struktury przełożonych.',
        loadError: 'Nie udało się wczytać listy. Odśwież stronę, aby spróbować ponownie.',
        removeDialog: {
          cancel: 'Anuluj',
          confirm: 'Usuń',
          conflict:
            'Ta osoba jest w komisji dnia egzaminacyjnego, który się jeszcze nie zakończył. Najpierw zmień skład komisji tego dnia.',
          description: 'Tej osoby nie będzie już można wybrać do komisji dnia egzaminacyjnego.',
          error: 'Nie udało się usunąć osoby z listy. Spróbuj ponownie.',
          title: 'Usunąć osobę z listy?',
        },
        removeTooltip: 'Usuń z listy',
        roles: {
          [CommissionCandidateRoleDto.Chairman]: 'Przewodniczący',
          [CommissionCandidateRoleDto.Hrbp]: 'HRBP',
        },
        title: 'Dodatkowi członkowie komisji',
      },
      individualExam: {
        cancelButton: 'Anuluj egzamin',
        carriedOver: {
          close: 'Zamknij',
          modalTitle: 'Termin egzaminu — źródło zaliczenia',
          previewSourceTooltip: 'Pokaż źródłowy termin egzaminu',
          question: 'Pytanie',
          report: 'Raport',
          subtitle:
            'Obszary oznaczone "global pass" zaliczone już w innym terminie. Nie podlegają ponownej ocenie, ale liczą się do wyniku i do progu zaliczenia tego egzaminu.',
          title: 'Zaliczone w poprzednich terminach',
        },
        chairmanComment: 'Komentarz przewodniczącego',
        changelog: 'Historia zmian',
        createdAt: 'Utworzono',
        dayNotStarted: 'najpierw rozpocznij dzień egzaminacyjny',
        dayNotStartedDialogCancel: 'Anuluj',
        dayNotStartedDialogConfirm: 'Rozpocznij dzień i egzamin',
        dayNotStartedDialogMessage:
          'Dzień egzaminacyjny nie został jeszcze rozpoczęty. Aby rozpocząć egzamin, musisz najpierw uruchomić dzień egzaminacyjny.',
        dayNotStartedDialogTitle: 'Dzień egzaminacyjny nie został rozpoczęty',
        editModeOff: 'Wyłącz edycję komentarzy',
        editModeOn: 'Edytuj komentarze',
        examStatus: {
          [IndividualExamStatusDto.Cancelled]: 'Anulowany',
          [IndividualExamStatusDto.Draft]: 'Szkic',
          [IndividualExamStatusDto.Failed]: 'Niezdany',
          [IndividualExamStatusDto.Passed]: 'Zdany',
          [IndividualExamStatusDto.Started]: 'Rozpoczęty',
        },
        failed: 'Niezdane',
        finishButton: 'Zakończ egzamin',
        finishedAtLabel: 'Zakończono',
        generalComment: 'Komentarze ogólne',
        generalCommentPlaceholder: 'Dodaj ogólny komentarz do egzaminu...',
        generalCommentVisibilityNotice: 'Te komentarze będą widoczne dla egzaminowanego',
        hrbpComment: 'Komentarz HRBP',
        infoAssignedQuestions: 'Przydzielone pytania',
        infoMinQuestions: 'Min. wymagane',
        infoQuestionsShort: 'pyt.',
        infoRequiredToPass: 'Wymagane do zdania',
        infoTimeLimit: 'Limit czasu',
        minRequired: 'min {{min}}, ocenionych {{current}}',
        notPassing: 'nie zdaje',
        passed: 'Zdane',
        passing: 'zdaje',
        progress: 'Postęp',
        questionsTitle: 'Pytania',
        result: 'Wynik',
        resultFailed: 'EGZAMIN NIEZDANY',
        resultPassed: 'EGZAMIN ZDANY',
        stage2AttemptsCount_few: '{{count}} podejścia',
        stage2AttemptsCount_many: '{{count}} podejść',
        stage2AttemptsCount_one: '{{count}} podejście',
        stage2AttemptsCount_other: '{{count}} podejść',
        stage2Failed: 'niezdany',
        stage2NotAvailable: 'Test teoretyczny nie został ukończony',
        stage2NoTerms: 'Uczestnik nie był przypisany do żadnego terminu',
        stage2Passed: 'zdany',
        stage2Result: 'Etap 2 — Test teoretyczny',
        stage2Status: {
          ABSENT: 'Nieobecny',
          COMPLETED: 'Zdany',
          FAILED: 'Niezdany',
          IN_PROGRESS: 'W trakcie',
          NOT_STARTED: 'Nie rozpoczęty',
        },
        stage2TermsListTitle: 'Test teoretyczny — podejścia',
        stage2ViewAttemptsTooltip: 'Zobacz wszystkie podejścia',
        stage2ViewTooltip: 'Zobacz odpowiedzi z testu teoretycznego',
        startButton: 'Rozpocznij egzamin',
        startedAtLabel: 'Rozpoczęto',
        total: 'Łącznie',
      },
      label: 'Etap testu praktycznego',
      loading: 'Ładowanie etapu egzaminacyjnego...',
      notInitialized:
        'Etap testu praktycznego nie został zainicjalizowany. Zainicjuj go ze strony szczegółów certyfikatu.',
      participants: {
        add: 'Dodaj',
        addButton: 'Dodaj uczestnika',
        addByEmployee: 'Z certyfikacji',
        addByPosition: 'Po stanowisku',
        addBySap: 'Po SAP',
        addedNoneNotFound: 'Żaden z numerów SAP nie został odnaleziony: {{identifiers}}',
        addedNoneTestNotPassed:
          'Nie dodano nikogo — wszyscy wskazani pracownicy nie mają zaliczonego testu (etap 2).',
        addedSummaryWithSkipped:
          'Dodano {{added}} uczestnika(-ów); pominięto {{skipped}} bez zaliczonego testu (etap 2).',
        addedSummaryWithSkippedSap:
          'Dodano {{added}} uczestnika(-ów); pominięto {{skipped}}: {{identifiers}}',
        addFromCertification: 'Dodaj z uczestników certyfikacji',
        addPositionButton: 'Dodaj {{count}} stanowisk(a)',
        addPositionCodesHelper:
          'Wpisz kody stanowisk oddzielone przecinkami lub spacjami (format: 2xxxxxxx lub 7xxxxxxx)',
        addPositionError: 'Błąd dodawania uczestników po stanowisku',
        addPositionInvalidFormat: 'Nieprawidłowy format: {{codes}}',
        addPositionPlaceholder: 'np. 20000001, 70000002',
        addSapButton: 'Dodaj {{count}} nr SAP',
        addSapError: 'Błąd dodawania uczestników po SAP',
        addSapHelper: 'Wpisz numery SAP oddzielone przecinkami lub spacjami (8-cyfrowy numer)',
        addSapInvalidFormat: 'Nieprawidłowy format: {{numbers}}',
        addSapPlaceholder: 'np. 00012345, 00067890',
        addSelectedButton: 'Dodaj {{count}} uczestnika(-ów)',
        addSuccess: 'Uczestnicy zostali dodani',
        columns: {
          actions: 'Akcje',
          addedAt: 'Data dodania',
          email: 'Email',
          examStatus: 'Status egzaminu',
          name: 'Imię i nazwisko',
        },
        empty: 'Nie dodano jeszcze uczestników',
        filtersClear: 'Wyczyść',
        filtersToggle: 'Filtry',
        helpTooltip:
          'Do testu praktycznego (etap 3) można dodać tylko pracowników, którzy mają zaliczony test (etap 2) w ramach tej samej certyfikacji.',
        noAvailableParticipants: 'Wszyscy uczestnicy certyfikacji zostali już dodani',
        noEligibleParticipants:
          'Brak uprawnionych uczestników — żaden z pracowników w tej certyfikacji nie ma jeszcze zaliczonego testu (etap 2).',
        removeTooltip: 'Usuń uczestnika',
        searchPlaceholder: 'Szukaj uczestników...',
        title: 'Uczestnicy',
      },
      practicalAttempts: {
        abandonButton: 'Odrzuć',
        abandonTooltip: 'Oznacz jako odrzucone',
        attemptChip: 'Podejście {{number}} · {{status}}',
        attemptsUsed: 'Wykorzystane podejścia: {{used}}/{{max}}',
        completeButton: 'Zakończ',
        completeTooltip: 'Oznacz jako zakończone',
        detail: {
          answerKey: 'Klucz odpowiedzi',
          emptyAttempt: 'Podejście nie zawiera pytań ani raportów',
          linkCopied: 'Link skopiowany do schowka',
          noAttachments: 'Brak załączników',
          pdfPreviewError: 'Nie udało się załadować podglądu PDF',
          pdfPreviewLoading: 'Ładowanie podglądu…',
          pdfPreviewPager: 'Strona {{page}} / {{total}}',
          questions: 'Pytania ({{count}})',
          reports: 'Raporty ({{count}})',
          shareButton: 'Udostępnij',
          shareError: 'Nie udało się utworzyć linku',
          title: 'Szczegóły podejścia {{number}}',
        },
        limitReached: 'Limit podejść wyczerpany',
        maxAttempts: 'Limit: {{count}} podejść',
        noParticipants: 'Brak uczestników do egzaminu praktycznego',
        passed: 'Zaliczone',
        startButton: 'Rozpocznij podejście',
        startError: 'Nie udało się rozpocząć podejścia',
        startSuccess: 'Podejście rozpoczęte',
        status: {
          [PracticalExamAttemptStatusDto.Abandoned]: 'Odrzucone',
          [PracticalExamAttemptStatusDto.Completed]: 'Zakończone',
          [PracticalExamAttemptStatusDto.InProgress]: 'W trakcie',
        },
        statusUpdateError: 'Nie udało się zaktualizować statusu',
        statusUpdateSuccess: 'Status zaktualizowany',
        title: 'Podejścia komisji',
      },
      questionPools: {
        attachButton: 'Dołącz pulę',
        browseTooltip: 'Przeglądaj pytania',
        detachError: 'Błąd odłączania puli',
        detachSuccess: 'Pula odłączona',
        detachTooltip: 'Odłącz pulę',
        empty: 'Brak dołączonych pul pytań. Prześlij nową lub dołącz istniejącą pulę.',
        poolCount: 'Pule:',
        questionsCount: 'Pytania:',
        title: 'Pule pytań',
        uploadButton: 'Prześlij pulę',
        uploadToPoolTooltip: 'Dodaj pytania do puli',
      },
      questions: {
        addButton: 'Dodaj pytanie',
        cancelButton: 'Anuluj',
        columns: {
          actions: 'Akcje',
          answerKey: 'Klucz odpowiedzi',
          question: 'Pytanie',
        },
        empty: 'Nie dodano jeszcze pytań',
        fields: {
          answerKey: 'Klucz odpowiedzi',
          questionText: 'Treść pytania',
        },
        generateSample: 'Generuj przykładowe (5)',
        removeTooltip: 'Usuń pytanie',
        saveButton: 'Dodaj',
        searchPlaceholder: 'Szukaj pytań...',
        title: 'Pytania',
      },
      tabs: {
        attempts: 'Podejścia',
        config: 'Konfiguracja',
        examDays: 'Dni egzaminacyjne',
        examDaysLockedReason:
          'Najpierw prześlij pytania, skonfiguruj etap oraz przypisz co najmniej jednego uczestnika',
        participants: 'Uczestnicy',
        questions: 'Pytania',
      },
      title: 'Test praktyczny',
    },
    examStageStatus: {
      [ExamStageStatusDto.Completed]: 'Zakończono',
      [ExamStageStatusDto.InProgress]: 'W trakcie',
      [ExamStageStatusDto.NotInitialized]: 'Nie zainicjowano',
      [ExamStageStatusDto.NotStarted]: 'Nie rozpoczęto',
    },
    filters: {
      allStatuses: 'Wszystkie statusy',
      searchButton: 'Szukaj',
      searchPlaceholder: 'Szukaj po nazwie...',
      status: 'Status',
    },
    formDialog: {
      cancelButton: 'Anuluj',
      clearDeadline: 'Wyczyść termin',
      createButton: 'Utwórz',
      createError: 'Błąd tworzenia certyfikatu',
      createSuccess: 'Certyfikat został utworzony',
      createTitle: 'Utwórz certyfikat',
      deadlineHelp: 'Opcjonalny termin ukończenia',
      deadlineLabel: 'Termin',
      deadlinePastConfirm: 'Wybrany termin jest w przeszłości. Czy na pewno chcesz go ustawić?',
      deadlinePastWarning: 'Termin jest w przeszłości',
      deadlineValidation: 'Termin musi być dzisiaj lub w przyszłości',
      description: 'Opis',
      descriptionMaxLength: 'Opis może mieć maksymalnie 200 znaków',
      editTitle: 'Edytuj certyfikat',
      nameLabel: 'Nazwa *',
      nameMaxLength: 'Nazwa może mieć maksymalnie 50 znaków',
      nameRequired: 'Nazwa jest wymagana',
      saveButton: 'Zapisz zmiany',
      savingButton: 'Zapisywanie...',
      status: 'Status',
      updateError: 'Błąd aktualizacji certyfikatu',
      updateSuccess: 'Certyfikat został zaktualizowany',
    },
    itemRow: {
      createdAt: 'Utworzono',
      deleteDisabledTooltip: 'Można usuwać tylko certyfikaty w statusie WERSJA ROBOCZA',
      deleteTooltip: 'Usuń',
      openTooltip: 'Otwórz',
    },
    loadError: 'Błąd ładowania certyfikatów',
    loading: 'Ładowanie certyfikatów...',
    newButton: 'Nowy certyfikat',
    practicalExam: {
      attachDialog: {
        attachButton: 'Przypisz',
        attachError: 'Błąd przypisywania puli',
        attachingButton: 'Przypisywanie...',
        attachSuccess: 'Pula przypisana',
        cancelButton: 'Anuluj',
        noPools: 'Brak pul egzaminu praktycznego',
        questionCount: 'pytań',
        searchButton: 'Szukaj',
        searchPlaceholder: 'Szukaj pul...',
        title: 'Przypisz pulę egzaminu praktycznego',
      },
      poolCard: {
        detachButton: 'Odłącz pulę',
        questionsLabel: 'pytań',
        showQuestions: 'Pokaż {{count}} pytań',
        uploadToPoolButton: 'Dodaj pytania do puli',
      },
      questionsBrowser: {
        addAttachmentButton: 'Dodaj PDF',
        answerKeyLabel: 'Klucz odpowiedzi',
        areaAll: 'Wszystkie obszary',
        attachmentDeleteError: 'Nie udało się usunąć załącznika',
        attachmentDeleteSuccess: 'Załącznik usunięty',
        attachmentsLabel: 'Załączniki',
        deleteAttachment: 'Usuń załącznik',
        downloadAttachment: 'Pobierz',
        globalPassLabel: 'Globalnie zaliczone',
        globalPassTooltip:
          'Jeśli włączone, pytanie/raport nie wraca do puli przy podejściu poprawkowym',
        noAttachments: 'Brak załączników',
        noQuestions: 'Brak pytań prostych',
        noReports: 'Brak raportów',
        previewAttachment: 'Podgląd',
        previewCloseButton: 'Zamknij',
        questionsSection: 'Pytania proste',
        reportChipLabel: 'RAPORT',
        reportsSection: 'Raporty',
        searchPlaceholder: 'Szukaj po treści lub kluczu odpowiedzi...',
        subquestionsLabel: 'Podpytania',
      },
      reportAttachmentDialog: {
        cancelButton: 'Anuluj',
        chooseFileButton: 'Wybierz PDF',
        dragDropText: 'Przeciągnij i upuść lub kliknij, aby wybrać plik PDF',
        errorToast: 'Błąd przesyłania',
        fileLabel: 'Plik PDF *',
        fileRequired: 'Plik jest wymagany',
        fileTooLarge: 'Plik jest za duży (maks. 10 MB)',
        hint: 'Dozwolony tylko PDF, maks. 10 MB.',
        invalidFileType: 'Dozwolone są tylko pliki PDF',
        successToast: 'Załącznik dodany',
        title: 'Dodaj załącznik do raportu "{{area}}"',
        uploadButton: 'Prześlij',
        uploadingButton: 'Przesyłanie...',
      },
      richKeyEditor: {
        uploadError: 'Nie udało się wgrać pliku {{filename}}. Spróbuj ponownie.',
        uploading: 'Wgrywanie pliku {{filename}}…',
      },
      uploadPoolDialog: {
        appendTitle: 'Prześlij do etapu egzaminu praktycznego',
        cancelButton: 'Anuluj',
        chooseFileButton: 'Wybierz plik',
        dragDropText: 'Przeciągnij i upuść lub kliknij, aby wybrać plik .xlsx',
        errorToast: 'Błąd przesyłania',
        fileLabel: 'Plik (XLSX) *',
        fileRequired: 'Plik jest wymagany',
        formatColumns:
          'Arkusz per obszar (OiW/Pieczywo/Lada): Sekcja | Podkategoria | Punktowana (TAK/NIE). Arkusz „Raporty": Nazwa raportu | Sekcja | Podkategoria | Punktowana.',
        formatTitle: 'Wymagany format (wielo-arkuszowy)',
        formatTypes:
          'Arkusz „_Globalne zaliczenie": Obszar | Globalne zaliczenie. Klucze odpowiedzi i pliki PDF uzupełniasz w aplikacji po wgraniu.',
        nameHelp: 'Opcjonalna nazwa puli (domyślnie „Pula pytań egzaminu praktycznego")',
        nameLabel: 'Nazwa puli',
        successToast: 'Pula przesłana pomyślnie',
        title: 'Prześlij pulę pytań egzaminu praktycznego',
        uploadButton: 'Prześlij',
        uploadingButton: 'Przesyłanie...',
        validationErrorsTitle: 'Błędy walidacji',
      },
      uploadToExistingPoolDialog: {
        cancelButton: 'Anuluj',
        chooseFileButton: 'Wybierz plik',
        dragDropText: 'Przeciągnij i upuść lub kliknij, aby wybrać plik .xlsx',
        errorToast: 'Błąd przesyłania',
        fileLabel: 'Plik (XLSX) *',
        fileRequired: 'Plik jest wymagany',
        successToast: 'Pytania dodane pomyślnie',
        title: 'Dodaj pytania egzaminu praktycznego do "{{poolName}}"',
        uploadButton: 'Prześlij',
        uploadingButton: 'Przesyłanie...',
        validationErrorsTitle: 'Błędy walidacji',
      },
    },
    questionPools: {
      filters: {
        allTypes: 'Wszystkie typy',
        search: 'Szukaj',
        searchPlaceholder: 'Szukaj po nazwie...',
      },
      table: {
        actions: 'Akcje',
        browseButton: 'Przeglądaj pytania',
        createdAt: 'Utworzono',
        name: 'Nazwa',
        noData: 'Nie znaleziono pul pytań',
        questionCount: 'Pytania',
        rowsPerPage: 'Wierszy na stronę',
        type: 'Typ',
        uploadButton: 'Prześlij pulę',
      },
      title: 'Pule pytań',
    },
    questionsBrowser: {
      explanationLabel: 'Wyjaśnienie',
      noResults: 'Nie znaleziono pytań',
      questionCount: 'pytań',
      rankLabel: 'Ranking',
      searchPlaceholder: 'Szukaj pytań...',
      sectionPlaceholder: 'Filtruj po obszarze',
      singleChoiceLabel: 'Jednokrotny wybór',
      tabPool: 'Ta pula',
      tabStage: 'Wszystkie pule',
      title: 'Przeglądaj pytania',
      typeAll: 'Wszystkie typy',
      typeRank: 'Ranking',
      typeSingleChoice: 'Jednokrotny wybór',
    },
    status: {
      [CertificationStatusDto.Active]: 'Aktywny',
      [CertificationStatusDto.Cancelled]: 'Anulowany',
      [CertificationStatusDto.Completed]: 'Ukończony',
      [CertificationStatusDto.Draft]: 'Wersja robocza',
    },
    stepCard: {
      configured: 'Skonfigurowany',
      instructionsTooltip: 'Pokaż instrukcję dla administratora',
      notConfigured: 'Nieskonfigurowany',
      stepLabel: 'Etap',
    },
    stepLabels: {
      comingSoon: 'wkrótce',
      developmentCenter: 'Development Center',
      devPlan: 'Plan rozwoju',
      knowledgeTest: 'Test wiedzy',
      notConfigured: 'Nieskonfigurowany',
      participants: '{{count}} uczestników',
      practicalTest: 'Test praktyczny',
      selfAssessment: 'Samoocena',
    },
    summary: {
      active: 'Aktywne',
      completed: 'Ukończone',
      draft: 'Wersja robocza',
      total: 'Łącznie',
    },
    surveyStep: {
      activationDialog: {
        cancel: 'Anuluj',
        confirm: 'Aktywuj i rozpocznij',
        description:
          'Aby uczestnicy mogli otrzymać powiadomienia o ankiecie, poniższe statusy muszą być aktywne. Czy aktywować je teraz i rozpocząć ankietę?',
        error: 'Błąd aktywacji statusów',
        itemCertification: 'Certyfikacja — zostanie aktywowana',
        itemStage: 'Etap 1 (Samoocena) — zostanie aktywowany',
        success: 'Statusy zaktywowane i ankieta rozpoczęta',
        title: 'Wymagana aktywacja statusów',
      },
      assignExisting: 'Przypisz istniejącą',
      changeButton: 'Zmień',
      changeSurveyTooltip: 'Zmień ankietę',
      createNew: 'Utwórz nową',
      deadlineLabel: 'Termin',
      deadlinePastTooltip: 'Termin minął — rozważ zmianę statusu na Zakończona.',
      detachButton: 'Odłącz',
      detachSurveyTooltip: 'Odłącz ankietę',
      noParticipants: 'Brak uczestników',
      noSurveyDescription: 'Przypisz istniejącą ankietę lub utwórz nową dla tego certyfikatu.',
      noSurveyTitle: 'Brak przypisanej ankiety',
      participants: 'uczestników',
      startDisabledTooltip: 'Dodaj uczestników, aby rozpocząć ankietę',
      status: {
        ACTIVE: 'Aktywna',
        CANCELLED: 'Anulowana',
        COMPLETED: 'Zakończona',
        DRAFT: 'Szkic',
      },
      statusAction: {
        cancel: 'Anuluj',
        complete: 'Zakończ',
        start: 'Rozpocznij',
      },
    },
    table: {
      actions: 'Akcje',
      createdBy: 'Utworzone przez',
      deadline: 'Termin',
      name: 'Nazwa',
      noData: 'Nie znaleziono certyfikatów',
      status: 'Status',
      steps: 'Etapy',
    },
    testStageConfig: {
      activateBlockedReason: 'Utwórz co najmniej jeden termin testu, aby aktywować ten etap',
      bar: {
        passPrefix: 'Próg',
        questionsUnit: 'pyt.',
        resultVisibilityWindow: 'Pytania widoczne {{minutes}} min po zakończeniu',
      },
      cancelButton: 'Anuluj',
      configureButton: 'Skonfiguruj test',
      editButton: 'Edytuj konfigurację',
      fields: {
        allowBackNavigation: 'Nawigacja wstecz',
        backNavEnabled: 'Nawigacja wstecz włączona',
        noLimit: 'Bez limitu',
        numberOfQuestions: 'Pytania',
        passingThreshold: 'Próg zaliczenia (%)',
        resultVisibility: 'Widoczność wyników',
        resultVisibilityWindowMinutes: 'Czas widoczności pytań po zakończeniu (min)',
        resultVisibilityWindowMinutesHint: 'Po tym czasie zostanie tylko wynik',
        timeExtensionHint: 'Maks. {{max}} min',
        timeExtensionMinutes: 'Wydłużenie czasu (min)',
        timeLimitMinutes: 'Limit czasu (min)',
      },
      label: 'Konfiguracja testu',
      notConfigured: 'Test nie został jeszcze skonfigurowany',
      pools: {
        attachButton: 'Dołącz istniejącą',
        attachError: 'Nie udało się odłączyć puli',
        browseButton: 'Przeglądaj pytania',
        detachBlockedByActiveTerm:
          'Nie można odłączyć puli — etap ma aktywny termin (NEW lub IN_PROGRESS). Anuluj lub zakończ wszystkie aktywne terminy.',
        detachButton: 'Odłącz',
        detachError: 'Nie udało się odłączyć puli',
        none: 'Brak dołączonych pul pytań',
        poolCount: 'Pule:',
        poolQuestionCount: 'Pytania:',
        questionsLabel: 'pytań',
        title: 'Pule pytań',
        uploadButton: 'Wgraj nową',
        uploadToPoolButton: 'Wgraj więcej pytań',
      },
      resultVisibilityOptions: {
        none: 'Brak (wyniki nieudostępnione)',
        resultOnly: 'Tylko wynik (zdany/niezdany)',
        resultWithQuestions: 'Wynik + pytania z oznaczeniem błędów',
        resultWithScore: 'Wynik + procent poprawnych odpowiedzi',
      },
      saveButton: 'Zapisz konfigurację',
      saveError: 'Nie udało się zapisać konfiguracji',
      saveSuccess: 'Konfiguracja zapisana',
      sections: {
        available: 'Dostępnych: {{count}} pytań',
        exceedsAvailable: 'Maks. {{count}} dostępnych',
        none: 'Nie skonfigurowano rozkładu obszarów',
        title: 'Rozkład obszarów',
        total: 'Łącznie: {{total}} pytań',
        totalLabel: 'Łączna liczba pytań',
      },
    },
    testStageDetail: {
      backButton: 'Wróć do certyfikacji',
      label: 'Etap testu wiedzy',
      loading: 'Ładowanie etapu testu...',
      notFound: 'Nie znaleziono etapu testu',
      notInitialized:
        'Etap testu nie został zainicjowany. Zainicjuj go ze strony szczegółów certyfikatu.',
      subtitle: 'Zarządzanie etapem — uczestnicy i terminy',
      tabs: {
        config: 'Konfiguracja testu',
        configLockedReason: 'Najpierw prześlij pulę pytań',
        participants: 'Uczestnicy etapu',
        participantsLockedReason: 'Najpierw zapisz konfigurację testu',
        pools: 'Pule pytań',
        terms: 'Terminy testów',
        termsLockedReason: 'Najpierw skonfiguruj etap oraz przypisz co najmniej jednego uczestnika',
      },
      title: 'Test wiedzy',
    },
    title: 'Certyfikaty',
    uploadPoolDialog: {
      appendTitle: 'Prześlij do istniejącego etapu',
      cancelButton: 'Anuluj',
      chooseFileButton: 'Wybierz plik',
      dragDropText: 'Przeciągnij i upuść lub kliknij, aby wybrać plik .xlsx',
      errorToast: 'Błąd przesyłania',
      fileLabel: 'Plik (XLSX) *',
      fileRequired: 'Plik jest wymagany',
      nameHelp: 'Opcjonalna nazwa (domyślnie nazwa arkusza)',
      nameLabel: 'Nazwa puli',
      successToast: 'Pula przesłana pomyślnie',
      title: 'Prześlij pulę pytań',
      uploadButton: 'Prześlij',
      uploadingButton: 'Przesyłanie...',
      validationErrorsTitle: 'Błędy walidacji',
    },
    uploadToExistingPoolDialog: {
      cancelButton: 'Anuluj',
      chooseFileButton: 'Wybierz plik',
      dragDropText: 'Przeciągnij i upuść lub kliknij, aby wybrać plik .xlsx',
      errorToast: 'Błąd przesyłania',
      fileLabel: 'Plik (XLSX) *',
      fileRequired: 'Plik jest wymagany',
      successToast: 'Pytania dodane pomyślnie',
      title: 'Dodaj pytania do "{{poolName}}"',
      uploadButton: 'Prześlij',
      uploadingButton: 'Przesyłanie...',
      validationErrorsTitle: 'Błędy walidacji',
    },
  },
  changeHistory: {
    filters: {
      action: 'Akcja',
      actions: {
        activate: 'Aktywuj',
        assign: 'Przypisz',
        create: 'Utwórz',
        deactivate: 'Dezaktywuj',
        delete: 'Usuń',
        toggleOverride: 'Przełącz nadpisanie',
        unassign: 'Cofnij przypisanie',
        update: 'Zaktualizuj',
      },
      all: 'Wszystkie',
      changedAfter: 'Zmieniono po',
      changedBefore: 'Zmieniono przed',
      changedByUserEmail: 'Zmieniono przez',
      changeType: 'Typ zmiany',
      changeTypes: {
        permissionEdit: 'Edycja uprawnienia',
        roleEdit: 'Edycja roli',
        rolePermissionAssignment: 'Przypisanie uprawnienia do roli',
        userPermissionChange: 'Zmiana uprawnień użytkownika',
        userRoleChange: 'Zmiana roli użytkownika',
      },
      clearFilters: 'Wyczyść filtry',
      targetUserEmail: 'Dotyczy użytkownika',
    },
    table: {
      actions: {
        ACTIVATE: 'Aktywuj',
        ASSIGN: 'Przypisz',
        CREATE: 'Utwórz',
        DEACTIVATE: 'Dezaktywuj',
        DELETE: 'Usuń',
        TOGGLE_OVERRIDE: 'Przełącz nadpisanie',
        UNASSIGN: 'Cofnij przypisanie',
        UPDATE: 'Zaktualizuj',
      },
      changeTypes: {
        PERMISSION_EDIT: 'Edycja uprawnienia',
        ROLE_EDIT: 'Edycja roli',
        ROLE_PERMISSION_ASSIGNMENT: 'Przypisanie uprawnienia do roli',
        USER_PERMISSION_CHANGE: 'Zmiana uprawnień użytkownika',
        USER_ROLE_CHANGE: 'Zmiana roli użytkownika',
      },
      error: 'Błąd podczas ładowania danych',
      fieldName: 'Pole',
      headers: {
        action: 'Akcja',
        changedAt: 'Data zmiany',
        changedBy: 'Zmieniono przez',
        changeType: 'Typ zmiany',
        description: 'Opis',
        id: 'ID',
        permission: 'Uprawnienie',
        role: 'Rola',
        targetUser: 'Dotyczy użytkownika',
      },
      loading: 'Ładowanie historii zmian...',
      newValue: 'Nowa wartość',
      noData: 'Brak danych do wyświetlenia',
      oldValue: 'Stara wartość',
      rowsPerPage: 'Wierszy na stronę',
    },
  },
  confirmPermissionAction: {
    actions: {
      activatePermission: {
        description:
          'Aktywacja uprawnienia sprawi, że będzie ono dostępne w systemie i będzie można je przypisywać do ról oraz użytkowników.',
        title: 'Czy na pewno chcesz aktywować to uprawnienie?',
        warning: '',
      },
      addIndividualPermission: {
        description:
          'Dodanie uprawnienia indywidualnego sprawi, że użytkownik otrzyma to uprawnienie niezależnie od przypisanych ról.',
        title: 'Czy na pewno chcesz dodać to uprawnienie indywidualne?',
        warning:
          'Uprawnienia indywidualne są nadawane bezpośrednio użytkownikowi i nie są zarządzane przez role.',
      },
      addRoleToUser: {
        description:
          'Dodanie roli sprawi, że użytkownik otrzyma wszystkie uprawnienia przypisane do tej roli.',
        title: 'Czy na pewno chcesz dodać tę rolę?',
        warning: '',
      },
      assignPermissionToRole: {
        description:
          'Przypisanie uprawnienia do roli sprawi, że wszyscy użytkownicy z tą rolą otrzymają to uprawnienie.',
        title: 'Czy na pewno chcesz przypisać to uprawnienie do roli?',
        warning: 'Zmiana wpłynie na wszystkich użytkowników, którzy mają przypisaną tę rolę.',
      },
      deactivatePermission: {
        description:
          'Dezaktywacja uprawnienia sprawi, że nie będzie ono dostępne w systemie i nie będzie można go przypisywać.',
        title: 'Czy na pewno chcesz dezaktywować to uprawnienie?',
        warning:
          'Uprawnienie pozostanie przypisane do istniejących ról i użytkowników, ale nie będzie aktywne.',
      },
      deactivateRolePermission: {
        description:
          'Dezaktywacja uprawnienia z roli sprawi, że użytkownik przestanie mieć to uprawnienie z tej roli, ale może je nadal posiadać z innych źródeł.',
        title: 'Czy na pewno chcesz dezaktywować to uprawnienie?',
        warning:
          'To nie usuwa uprawnienia z roli, tylko wyłącza je dla tego konkretnego użytkownika.',
      },
      removeIndividualPermission: {
        description:
          'Usunięcie uprawnienia indywidualnego sprawi, że użytkownik przestanie mieć to uprawnienie (chyba że posiada je z przypisanej roli).',
        title: 'Czy na pewno chcesz usunąć to uprawnienie indywidualne?',
        warning: '',
      },
      removePermissionFromRole: {
        description:
          'Usunięcie uprawnienia z roli sprawi, że wszyscy użytkownicy z tą rolą przestaną mieć to uprawnienie.',
        title: 'Czy na pewno chcesz usunąć to uprawnienie z roli?',
        warning: 'Zmiana wpłynie na wszystkich użytkowników, którzy mają przypisaną tę rolę.',
      },
      removeRoleFromUser: {
        description:
          'Usunięcie roli sprawi, że użytkownik przestanie mieć wszystkie uprawnienia przypisane do tej roli.',
        title: 'Czy na pewno chcesz usunąć tę rolę?',
        warning: 'Nie można usunąć ról systemowych, można je tylko dezaktywować.',
      },
      toggleRole: {
        description:
          'Zmiana statusu roli sprawi, że wszystkie uprawnienia z tej roli będą włączone/wyłączone dla tego użytkownika.',
        title: 'Czy na pewno chcesz zmienić status tej roli?',
        warning: 'Dotyczy tylko ról systemowych.',
      },
    },
    cancel: 'Anuluj',
    confirm: 'Potwierdź',
  },
  dictionaries: {
    editDialog: {
      active: 'Aktywny',
      cancel: 'Anuluj',
      englishName: 'Angielska nazwa',
      polishName: 'Polska nazwa',
      save: 'Zapisz',
      saving: 'Zapisywanie...',
      title: 'Edytuj słownik',
    },
    filters: {
      active: 'Aktywny',
      allStatuses: 'Wszystkie statusy',
      allTypes: 'Wszystkie typy',
      inactive: 'Nieaktywny',
      name: 'Nazwa',
      searchByName: 'Szukaj według nazwy',
      status: 'Status',
      type: 'Typ',
    },
    table: {
      actions: 'Akcje',
      code: 'Kod',
      edit: 'Edytuj',
      englishName: 'Nazwa angielska',
      noData: 'Brak danych pasujących do wybranych filtrów',
      noFilters: 'Wybierz typ słownika, aby wyświetlić dane',
      polishName: 'Nazwa polska',
      rowsPerPage: 'Wierszy na stronę',
      status: {
        false: 'Nieaktywny',
        true: 'Aktywny',
      },
      statusHeader: 'Status',
      type: 'Typ',
    },
    title: 'Słowniki',
  },
  employeesList: {
    buttons: {
      export: 'Eksportuj do Excel',
    },
    content: {
      table: {
        active: 'Aktywny',
        email: 'Email',
        externalEmployeeId: 'SAP',
        firstName: 'Imię',
        inactive: 'Nieaktywny',
        lastName: 'Nazwisko',
        openProfile: 'Otwórz profil pracownika',
        openProfileUnavailable: 'Brak uprawnień do przeglądania profilu',
      },
    },
    dialog: {
      activateInfo:
        'Aktywacja przywróci role systemowe wynikające z danych kadrowych tej osoby (Pracownik, Przełożony P1/P2, HRBP).',
      cancel: 'Anuluj',
      confirm: 'Potwierdź',
      deactivateInfo:
        'Dezaktywacja odbierze tej osobie role systemowe (Pracownik, Przełożony P1/P2, HRBP) wraz z wynikającym z nich dostępem. Role nadane ręcznie, np. Administrator, pozostaną bez zmian.',
      message: 'Czy na pewno chcesz zmienić status aktywności tego pracownika?',
      title: 'Potwierdź zmianę statusu',
    },
    form: {
      externalEmployeeId: 'SAP',
      firstName: 'Imię',
      lastName: 'Nazwisko',
    },
  },
  groups: {
    certification: 'Certyfikacja',
    content: 'Treści',
    management: 'Zarządzanie',
    tools: 'Narzędzia',
  },
  impersonation: {
    blockedDisabled: 'To konto jest nieaktywne, więc nie można się na nie przełączyć.',
    blockedNoRole: 'Ta osoba nie ma przypisanej żadnej roli, więc nie można się na nią przełączyć.',
    blockedPrivileged:
      'Ta osoba ma uprawnienia administracyjne (zarządzanie rolami lub przełączanie sesji), więc nie można się na nią przełączyć.',
    noEmployeeRecordChip: 'brak konta pracownika',
    noRoleChip: 'brak roli',
    privilegedChip: 'uprawnienia administracyjne',
  },
  legalDocuments: {
    currentBadge: 'obowiązuje',
    delete: 'Usuń wersję',
    deleteDialog: {
      cancel: 'Anuluj',
      confirm: 'Usuń',
      message: 'Ta wersja zostanie trwale usunięta razem z plikiem.',
      title: 'Usunąć tę wersję?',
    },
    dropzoneHintPrefix: 'Plik PDF, maksymalnie',
    dropzoneLabel: 'Przeciągnij nowy regulamin lub wybierz plik',
    dropzoneMaxSize: '8 MB',
    empty:
      'Nie opublikowano jeszcze żadnej wersji. Wgraj plik PDF, aby regulamin był dostępny dla użytkowników.',
    info: 'Opublikowana wersja od razu zastępuje poprzednią pod tym samym odnośnikiem w stopce aplikacji. Wcześniejsze wersje zostają tutaj i można je przywrócić.',
    preview: 'Otwórz plik',
    setCurrent: 'Ustaw jako obowiązującą',
    versionLabel: 'Wersja {{version}}',
    versionsTitle: 'Opublikowane wersje',
  },
  news: {
    actions: {
      archive: 'Archiwizuj',
      backToDraft: 'Przenieś do szkicu',
      cancel: 'Anuluj',
      delete: 'Usuń',
      edit: 'Edytuj',
      error: 'Nie udało się zmienić statusu aktualności. Spróbuj ponownie.',
      preview: 'Podgląd',
      publish: 'Opublikuj',
    },
    categories: {
      [NewsCategoryDto.Business]: 'Rozwój',
      [NewsCategoryDto.System]: 'Aktualizacje jAIn',
      [NewsCategoryDto.Tutorials]: 'Tutoriale (Samouczki)',
    },
    confirmations: {
      archive: {
        confirm: 'Archiwizuj',
        message: 'Artykuł zostanie zarchiwizowany i zniknie z aktualności na stronie głównej.',
        title: 'Archiwizuj aktualność',
      },
      delete: {
        confirm: 'Usuń',
        message: 'Czy na pewno chcesz usunąć ten artykuł?',
        title: 'Usuń aktualność',
      },
      draft: {
        confirm: 'Przenieś do szkicu',
        message: 'Artykuł przestanie być widoczny na listach opublikowanych aktualności.',
        title: 'Przenieś aktualność do szkicu',
      },
      publish: {
        confirm: 'Opublikuj',
        message: 'Artykuł będzie widoczny w aktualnościach na stronie głównej.',
        title: 'Opublikuj aktualność',
      },
    },
    editor: {
      cancel: 'Anuluj',
      categoryLabel: 'Kategoria',
      categoryRequired: 'Kategoria jest wymagana',
      changeImage: 'Zmień obraz',
      chooseImage: 'Wybierz obraz',
      contentHelp: '',
      contentLabel: 'Treść',
      createError: 'Nie udało się utworzyć aktualności.',
      createTitle: 'Tworzenie nowej publikacji',
      editTitle: 'Edycja publikacji',
      imageClear: 'Wyczyść',
      imageDropActive: 'Upuść obraz tutaj',
      imageDropReplace: 'Upuść lub kliknij, aby zmienić obraz',
      imageDropSubtitle: 'Akceptowane formaty: JPEG, PNG, WebP',
      imageDropTitle: 'Przeciągnij i upuść obraz lub kliknij, aby wybrać',
      imageHelp:
        'Opcjonalnie. JPEG, PNG lub WebP, maksymalnie 10 MB. Obraz zostanie wstawiony w treść artykułu po zapisie.',
      imageLabel: 'Obraz w treści',
      imageSelected: 'Wybrane obrazy:',
      imageSizeError: 'Obraz może mieć maksymalnie 10 MB.',
      imageTypeError: 'Użyj obrazu JPEG, PNG lub WebP.',
      loading: 'Ładowanie artykułu...',
      publishNow: 'Opublikuj teraz',
      save: 'Zapisz',
      saveChanges: 'Zapisz zmiany',
      saveDraft: 'Zapisz jako szkic',
      saving: 'Zapisywanie...',
      subtitle: 'Przygotuj treść artykułu i opublikuj go, gdy będzie gotowy.',
      titleLabel: 'Tytuł',
      titleMaxLength: 'Tytuł może mieć maksymalnie 180 znaków',
      titlePlaceholder: 'np. Nowe zasady raportowania...',
      titleRequired: 'Tytuł jest wymagany',
      toolbar: {
        bold: 'Pogrubienie',
        image: 'Wstaw obraz',
        italic: 'Kursywa',
        link: 'Wklej adres linku',
        linkDialog: {
          apply: 'Zastosuj link',
          cancel: 'Anuluj',
          title: 'Wstaw link',
        },
        orderedList: 'Lista numerowana',
        underline: 'Podkreślenie',
        unorderedList: 'Lista punktowana',
        video: 'Wstaw wideo (wspierane formaty: MP4, WebM)',
      },
      untitledDraft: 'Szkic bez tytułu',
      updateError: 'Nie udało się zaktualizować aktualności.',
      videoError:
        'Nie można odtworzyć tego wideo. Plik może być uszkodzony albo chwilowo niedostępny.',
      videoProcessing:
        'Wideo zostało wgrane i jest przetwarzane. Pojawi się samo, gdy przetwarzanie się zakończy — nie musisz wgrywać go ponownie.',
      videoProcessingFailed:
        'Przetwarzanie tego wideo nie powiodło się. Usuń je z treści i wgraj plik ponownie.',
      videoTypeError: 'Użyj wideo MP4 albo WebM. QuickTime/MOV nie jest obsługiwany w Firefox.',
    },
    empty: {
      title: 'Nie znaleziono aktualności',
    },
    filters: {
      allCategories: 'Wszystkie kategorie',
      allStatuses: 'Wszystkie statusy',
      category: 'Kategoria',
      searchPlaceholder: 'Szukaj aktualności po tytule...',
      status: 'Status',
    },
    newButton: 'Nowy artykuł',
    preview: {
      close: 'Zamknij',
      emptyContent: 'Nie dodano jeszcze treści.',
      subtitle: 'Zobacz, jak aktualność będzie widoczna dla pracowników na stronie głównej.',
      title: 'Podgląd aktualności',
      videoError:
        'Nie można odtworzyć tego wideo. Plik może być uszkodzony albo niepoprawnie zakodowany.',
    },
    statuses: {
      [NewsStatusDto.Archived]: 'Zarchiwizowany',
      [NewsStatusDto.Draft]: 'Szkic',
      [NewsStatusDto.Published]: 'Opublikowany',
    },
    subtitle: 'Twórz, edytuj, publikuj i archiwizuj artykuły.',
    table: {
      actions: 'Akcje',
      category: 'Kategoria',
      createdAt: 'Utworzono',
      error: 'Błąd podczas ładowania aktualności',
      loading: 'Ładowanie aktualności...',
      noData: 'Brak aktualności dla wybranych filtrów.',
      noImage: 'Brak obrazu',
      publishedAt: 'Opublikowano',
      rowsPerPage: 'Wierszy na stronę',
      status: 'Status',
      title: 'Tytuł',
      updatedAt: 'Zaktualizowano',
    },
    title: 'Zarządzanie aktualnościami',
  },
  noPermissions: 'Nie masz uprawnień do żadnej sekcji panelu administracyjnego.',
  permissionDetails: {
    active: 'Aktywne',
    category: 'Kategoria',
    code: 'Kod',
    description: 'Opis',
    inactive: 'Nieaktywne',
    name: 'Nazwa',
    noDescription: 'Brak opisu',
    status: 'Status uprawnienia',
    title: 'Szczegóły uprawnienia',
  },
  permissionsEmployees: {
    backToList: 'Powrót do listy',
    columns: {
      department: 'Dział',
      email: 'Email',
      employee: 'Pracownik',
      sap: 'SAP',
      status: 'Status',
    },
    description: 'Wyszukaj pracownika, aby zarządzać jego rolami i uprawnieniami.',
    detail: {
      activate: 'Aktywuj',
      allWithCount: 'Wszystkie ({{count}})',
      assignedPermissions: 'Przypisane uprawnienia',
      assignedRoles: 'Przypisane role ({{count}})',
      availablePermissions: 'Dostępne uprawnienia',
      availableRoles: 'Dostępne role ({{count}})',
      categoryNone: 'Brak',
      categoryUncategorized: 'Bez kategorii',
      deactivate: 'Dezaktywuj',
      fromRole: 'z roli: {{role}}',
      hrbpTitle: 'Uprawnienia jednostka / stanowisko',
      individualPermissions: 'Uprawnienia indywidualne',
      noAssignedPermissions: 'Brak przypisanych uprawnień',
      noAssignedRoles: 'Brak przypisanych ról',
      noAvailablePermissions: 'Brak dostępnych uprawnień',
      noAvailableRoles: 'Brak dostępnych ról',
      noPermissionsMatch: 'Nie znaleziono uprawnień spełniających kryteria.',
      noResults: 'Brak wyników',
      noRolePermissions: 'Brak uprawnień z ról.',
      permissionCount: 'Liczba uprawnień: {{count}}',
      permissionsTitle: 'Uprawnienia',
      roleDisableSuffix: 'wyłączenie',
      roleEnableSuffix: 'włączenie',
      rolePermissions: 'Uprawnienia z ról',
      rolesTitle: 'Role pracownika',
      searchByNameOrCode: 'Szukaj po nazwie lub kodzie',
      statusActive: 'Aktywne',
      statusDisabled: 'Wyłączone',
      systemBadge: 'SYSTEM',
      systemRolesInfoBody:
        'Role systemowe są nadawane automatycznie i nie można ich usunąć. Można je tylko włączyć/wyłączyć dla konkretnego pracownika.',
      systemRolesInfoTitle: 'Role systemowe',
      toast: {
        bulkPartial:
          'Zaktualizowano {{success}} z {{total}} uprawnień. Sprawdź szczegóły w konsoli.',
        cannotRemoveSystemRole: 'Nie można usunąć roli systemowej. Możesz ją tylko dezaktywować.',
        individualsAdded: 'Dodano uprawnienia indywidualne',
        individualsAddError: 'Nie udało się dodać uprawnień indywidualnych',
        individualsRemoved: 'Usunięto uprawnienia indywidualne',
        individualsRemoveError: 'Nie udało się usunąć uprawnień indywidualnych',
        noRolesSelected: 'Nie wybrano żadnych ról do aktualizacji',
        permissionActivated: 'Uprawnienie "{{name}}" zostało aktywowane',
        permissionActivatedForRoles: 'Uprawnienie "{{name}}" zostało aktywowane dla ról: {{roles}}',
        permissionDeactivated: 'Uprawnienie "{{name}}" zostało dezaktywowane',
        permissionDeactivatedForRoles:
          'Uprawnienie "{{name}}" zostało dezaktywowane dla ról: {{roles}}',
        permissionUpdateError: 'Nie udało się zaktualizować uprawnienia',
        roleAdded: 'Rola "{{name}}" została dodana',
        roleAddError: 'Nie udało się dodać roli do użytkownika',
        roleDisabled: 'Rola "{{name}}" została wyłączona',
        roleEnabled: 'Rola "{{name}}" została włączona',
        roleNotFoundForPermission: 'Nie znaleziono roli dla tego uprawnienia',
        rolePermissionUpdateError: 'Nie udało się zaktualizować uprawnienia z ról',
        roleRemoved: 'Rola została usunięta',
        roleRemoveError: 'Nie udało się usunąć roli od użytkownika',
        roleUpdateError: 'Nie udało się zaktualizować roli użytkownika',
      },
    },
    emptyResultsDescription: 'Nie znaleziono pracowników pasujących do podanych kryteriów.',
    emptyResultsTitle: 'Brak wyników',
    loading: 'Ładowanie pracowników...',
    onlyModified: 'Tylko zmodyfikowani',
    rowsPerPage: 'Na stronie:',
    searchPlaceholder: 'Wyszukaj po SAP, imieniu, nazwisku, email...',
    startDescription: 'Wpisz frazę wyszukiwania lub włącz filtr, aby wyświetlić pracowników.',
    startTitle: 'Zacznij wyszukiwanie',
    statusModified: 'Zmienione',
    title: 'Pracownicy — Role i Uprawnienia',
  },
  permissionsTable: {
    active: 'Aktywne',
    allCategories: 'Wszystkie kategorie',
    columnActions: 'Akcje',
    columnCategory: 'Kategoria',
    columnCode: 'Kod',
    columnDescription: 'Opis',
    columnName: 'Nazwa',
    columnStatus: 'Status',
    count: '{{filtered}} z {{total}} uprawnień',
    deleteCentralInfo: 'Usuwanie uprawnienia "{{code}}" jest zarządzane centralnie.',
    editCentralInfo: 'Edycja uprawnienia "{{code}}" jest dostępna tylko w panelu systemowym.',
    emptyDescription: 'W systemie nie zdefiniowano jeszcze żadnych uprawnień.',
    emptyFilteredDescription:
      'Nie znaleziono uprawnień pasujących do podanych kryteriów. Spróbuj zmienić filtry.',
    emptyFilteredTitle: 'Brak wyników',
    emptyTitle: 'Brak uprawnień',
    inactive: 'Nieaktywne',
    loading: 'Ładowanie uprawnień...',
    searchPlaceholder: 'Kod, nazwa lub opis...',
    toggleError: 'Nie udało się zaktualizować uprawnienia',
    toggleSuccess: 'Zaktualizowano status uprawnienia',
  },
  professionalActivityDefinitions: {
    documents: {
      aiContext: 'Kontekst AI',
      aiContextTooltip:
        'Gdy włączone, dokument zasila asystenta AI u przypisanych pracowników. Wyłączone = tylko do pobrania.',
      deleteDialog: {
        cancel: 'Anuluj',
        confirm: 'Usuń',
        message: 'Ten plik zostanie trwale usunięty. Tej operacji nie można cofnąć.',
        title: 'Usuń dokument',
      },
      dropzoneHintPrefix: 'lub przeciągnij plik tutaj.',
      dropzoneLabel: 'Kliknij, aby wgrać plik',
      dropzoneMaxSize: 'Maksymalny rozmiar: 20 MB',
      embeddingStatus: {
        available: '✓ Dostępny w AI',
        failed: 'Błąd przetwarzania',
        noContent: 'Brak treści',
        notEmbedded: 'Nieprzetworzony',
        processing: 'Przetwarzanie...',
      },
      empty: 'Nie dodano jeszcze żadnych dokumentów',
      loading: 'Ładowanie dokumentów...',
      reembed: 'Przetwórz ponownie',
      title: 'Dokumenty',
    },
    editDialog: {
      cancel: 'Anuluj',
      description: 'Opis',
      developmentProgram: 'Program rozwojowy',
      editYear: 'Rok edycji',
      name: 'Nazwa',
      save: 'Zapisz',
      saving: 'Zapisywanie...',
      status: 'Status',
      statusValues: {
        COMPLETED: 'Zakończona',
        IN_PROGRESS: 'W toku',
        PLANNED: 'Planowana',
      },
      title: 'Edytuj czynność zawodową',
    },
    searchPlaceholder: 'Szukaj po nazwie',
    table: {
      description: 'Opis',
      developmentProgram: 'Program rozwojowy',
      editYear: 'Rok edycji',
      name: 'Nazwa',
      noData: 'Nie znaleziono czynności zawodowych',
      rowsPerPage: 'Wierszy na stronę',
      status: 'Status',
      statusValues: {
        COMPLETED: 'Zakończona',
        IN_PROGRESS: 'W toku',
        PLANNED: 'Planowana',
      },
      updatedAt: 'Zaktualizowano',
    },
    title: 'Aktywności rozwojowe',
  },
  ragDocuments: {
    form: {
      fields: {
        categories: {
          label: 'Kategorie',
          placeholder: 'Wybierz kategorie',
        },
        description: {
          label: 'Opis',
        },
        file: {
          accepted: 'Akceptowane formaty: PDF, DOCX (max 10MB)',
          dropzoneLabel: 'Przeciągnij i upuść plik lub kliknij aby wybrać',
          remove: 'Usuń',
        },
        tags: {
          label: 'Tagi',
          placeholder: 'Wpisz tag i naciśnij Enter',
        },
        title: {
          label: 'Tytuł',
        },
      },
      status: {
        awaitingValidationResolution: 'Oczekiwanie na rozwiązanie walidacji',
        close: 'Zamknij',
        error: 'Błąd wgrywania',
        finished: 'Wgrywanie zakończone',
        inProgress: 'Przetwarzanie',
        notStarted: 'Przygotowanie do wgrania',
        processing: 'Przetwarzanie dokumentu...',
        readingDocument: 'Odczytywanie dokumentu',
        validationFailed: 'Błąd walidacji',
      },
      submitButton: 'Wgraj dokument',
      submitButtonLoading: 'Wgrywanie...',
      title: 'Wgraj nowy dokument',
      toasts: {
        initError: 'Błąd podczas inicjowania wgrywania dokumentu',
        sseError: 'Utracono połączenie z serwerem postępu wgrywania',
        uploadError: 'Błąd podczas wgrywania dokumentu',
        uploadSuccess: 'Dokument został wgrany.',
      },
    },
  },
  roleAssignments: {
    allWithCount: 'Wszystkie ({{count}})',
    assignAriaLabel: 'Przypisz zaznaczone uprawnienia',
    assignedTitle: 'Uprawnienia przypisane – {{role}}',
    assignError: 'Nie udało się dodać uprawnień do roli',
    assignSuccess: 'Dodano uprawnienia do roli',
    availableTitle: 'Dostępne uprawnienia',
    categoryWithCount: '{{category}} ({{count}})',
    confirmDetails: 'Liczba uprawnień: {{count}}\nRola: {{role}}',
    count: '{{filtered}} z {{total}} uprawnień',
    emptyAssigned: 'Brak uprawnień przypisanych do tej roli.',
    emptyAvailable: 'Brak dostępnych uprawnień do przypisania.',
    emptyFiltered: 'Brak uprawnień spełniających kryteria filtrowania.',
    noRoles: 'Brak dostępnych ról.',
    removeAriaLabel: 'Usuń zaznaczone uprawnienia',
    removeError: 'Nie udało się usunąć uprawnień z roli',
    removeSuccess: 'Usunięto uprawnienia z roli',
    rolePlaceholder: '---',
    rolesTitle: 'Role',
    searchPlaceholder: 'Szukaj po nazwie lub kodzie',
    selectAll: 'Zaznacz wszystkie',
    selectedCount: 'Zaznaczono: {{count}}',
  },
  roleForm: {
    cancel: 'Anuluj',
    colorHint: 'Kolor używany w kartach i wykresach.',
    colorLabel: 'Kolor',
    createTitle: 'Nowa rola',
    descriptionLabel: 'Opis roli',
    editTitle: 'Edytuj rolę',
    nameLabel: 'Nazwa roli',
    save: 'Zapisz',
  },
  roles: {
    badgePermissions: '{{count}} uprawnień',
    badgePermissionsSystem: '{{count}} uprawnień • rola systemowa',
    count: '{{count}} ról',
    emptyDescription: 'Nie znaleziono żadnych ról w systemie.',
    emptySearchDescription:
      'Nie znaleziono ról pasujących do "{{search}}". Spróbuj zmienić kryteria wyszukiwania.',
    emptySearchTitle: 'Brak wyników wyszukiwania',
    emptyTitle: 'Brak ról',
    loading: 'Ładowanie ról...',
    searchPlaceholder: 'Szukaj roli',
    updateError: 'Nie udało się zaktualizować roli',
    updateSuccess: 'Zaktualizowano rolę',
  },
  sections: {
    accounts: {
      description: 'Wyszukuj, weryfikuj i zarządzaj dostępem do kont systemowych.',
    },
    data: {
      historyDescription:
        'Sprawdzaj status importów, błędy walidacji i ostatnią aktywność przesyłania plików.',
      historyTitle: 'Historia uploadów',
      uploadDescription:
        'Wgrywaj pliki z danymi pracowników, organizacji i systemu z jednego panelu.',
      uploadTitle: 'Wgraj dane',
    },
    developmentCycles: {
      applyToAllChip: 'Obejmuje wszystkich',
      campaignStatus: {
        [DevelopmentCycleCampaignStatusDto.Cancelled]: 'Anulowana',
        [DevelopmentCycleCampaignStatusDto.Draft]: 'Szkic',
        [DevelopmentCycleCampaignStatusDto.Finished]: 'Zakończona',
        [DevelopmentCycleCampaignStatusDto.InProgress]: 'W trakcie',
      },
      campaignType: {
        CERTIFICATION: 'Certyfikacja',
        DEFAULT: 'Zwykła',
        MDR: 'MDR',
      },
      cancelCampaign: {
        cancel: 'Anuluj',
        confirm: 'Tak, anuluj kampanię',
        description:
          'Czy na pewno anulować kampanię „{{name}}”? Wszystkie jej plany rozwojowe (łącznie ze zrealizowanymi) trafią do historycznych, a ich otwarte zadania zostaną anulowane.',
        notice:
          'Anulowanie to nie to samo co zakończenie. Zakończenie zamyka każdy plan osobno, zależnie od odhaczonych kroków, a anulowanie archiwizuje wszystkie. W obu przypadkach pracownicy przestają być objęci kampanią. Plan historyczny da się przywrócić, gdy pracownika znów obejmie ta kampania.',
        title: 'Anuluj kampanię',
      },
      delete: {
        cancel: 'Anuluj',
        confirm: 'Usuń kampanię',
        description:
          'Czy na pewno chcesz usunąć kampanię „{{name}}”? Tej operacji nie można cofnąć.',
        title: 'Usuń kampanię',
      },
      description:
        'Kampanie cyklu rozwojowego: okno, data startu zadań i objęci pracownicy. Aktywnych kampanii może być kilka, ale pracownik należy do dokładnie jednej.',
      fields: {
        certification: 'Powiązana certyfikacja',
        certificationFallback: 'Przejdź do certyfikacji',
        covered: 'Numery SAP objętych pracowników',
        coveredAll: 'Wszyscy pracownicy nieobjęci inną kampanią. Ta kampania nie tworzy zadań.',
        coveredEmpty:
          'Pusta lista — kampania nie obejmuje nikogo, więc nie powstaną żadne zadania.',
        developmentDirection: 'Kierunek rozwoju',
        name: 'Nazwa',
        noName: '(bez nazwy)',
        tasksStartDate: 'Zadania tworzone od',
        window: 'Okno cyklu',
      },
      filters: {
        anyStatus: 'Dowolny status',
        anyType: 'Dowolny rodzaj',
        searchPlaceholder: 'Nazwa kampanii lub numer SAP',
        statusLabel: 'Status',
        typeLabel: 'Rodzaj',
      },
      form: {
        applyToAll: 'Obejmij wszystkich',
        applyToAllHint:
          'Kampania obejmie każdego, kogo nie obejmuje inna kampania. W trakcie może być tylko jedna taka kampania.',
        applyToAllNotice:
          'Kampania ogólna nie tworzy zadań — otwiera tylko możliwość samodzielnego założenia planu. Zadania powstają wyłącznie w kampaniach z listą pracowników.',
        campaignType: 'Typ kampanii',
        campaignTypeHint: {
          CERTIFICATION:
            'Wszyscy uczestnicy dostają ten sam kierunek rozwoju, a pole kierunku znika z formularza planu. Można wskazać certyfikację, z której wzięli się uczestnicy.',
          DEFAULT: 'Kampania bez dodatkowych ograniczeń — działa jak dotąd.',
          MDR: 'Obejmuje wyłącznie osoby w procesie MDR (z wypełnioną macierzą 7 pól). Numery SAP są sprawdzane przy dodawaniu.',
        },
        campaignTypeOption: {
          CERTIFICATION: 'Kampania certyfikacyjna',
          DEFAULT: 'Zwykła kampania',
          MDR: 'Kampania MDR',
        },
        cancel: 'Anuluj',
        certification: 'Powiązana certyfikacja',
        certificationEmpty: 'Brak certyfikacji pasujących do wyszukiwania.',
        certificationHint:
          'Opcjonalne. Dzięki temu z kampanii da się przejść wprost do certyfikacji, z której wzięli się jej uczestnicy.',
        certificationPlaceholder: 'Zacznij pisać nazwę certyfikacji',
        createTitle: 'Nowa kampania',
        cycleValidFrom: 'Cykl ważny od',
        cycleValidTo: 'Cykl ważny do',
        developmentDirection: 'Kierunek rozwoju',
        developmentDirectionHint:
          'Wspólny dla wszystkich uczestników kampanii. Pracownik nie poda własnego — w formularzu planu zobaczy ten kierunek jako ustalony przez administratora.',
        developmentDirectionPlaceholder: 'np. Kierownik Zmiany',
        editTitle: 'Edytuj kampanię',
        errors: {
          conflict:
            'Sprawdź daty kampanii oraz czy numery SAP nie należą już do innej aktywnej kampanii.',
          developmentDirection: 'Kampania certyfikacyjna wymaga podania kierunku rozwoju.',
          generic: 'Nie udało się zapisać kampanii.',
          tasksStartDate: 'Data startu zadań musi mieścić się w oknie cyklu (od–do).',
          window: 'Data „ważny do” musi być późniejsza niż „ważny od”.',
        },
        name: 'Nazwa kampanii',
        namePlaceholder: 'Opcjonalna, np. Cykl 2026 H1',
        sapCheck: {
          accepted: 'Kampania przyjmuje {{acceptedCount}} z {{total}} numerów.',
          button: 'Sprawdź numery',
          checking: 'Sprawdzam...',
          copied: 'Skopiowano',
          copyFailed: 'Nie udało się skopiować listy. Zaznacz ją i skopiuj ręcznie.',
          copyRejected: 'Kopiuj odrzucone numery',
          error: 'Nie udało się sprawdzić numerów. Spróbuj ponownie.',
          reason: {
            ALREADY_IN_OTHER_CAMPAIGN: 'jest już w innej trwającej kampanii',
            NOT_IN_MDR_PROCESS: 'nie jest w procesie MDR',
            UNKNOWN_SAP: 'nie ma pracownika o tym numerze',
          },
          rejectedRow: '{{sap}} — {{reason}}',
          rejectedTitle: 'Odrzucone: {{rejectedCount}}',
        },
        sapImport: {
          clearConfirmAction: 'Wyczyść',
          clearConfirmBody:
            'Usuniesz {{sapCount}} numerów z listy. Tej operacji nie da się cofnąć.',
          clearConfirmTitle: 'Wyczyścić całą listę?',
          clearList: 'Wyczyść listę',
          dropzoneBottom: 'Obsługiwany format: XLSX',
          dropzoneTop: 'Przeciągnij plik XLSX lub kliknij, aby wybrać',
          errorTooMany:
            'Plik zawiera więcej niż 10 000 numerów SAP. Podziel go na mniejsze części i wczytaj ponownie.',
          errorUnreadable: 'Nie udało się odczytać pliku. Upewnij się, że to poprawny plik XLSX.',
          fileHint:
            'Jeden numer w wierszu, w pierwszej kolumnie. Numery muszą być zapisane dokładnie tak jak w danych kadrowych. Jeśli zaczynają się od zera, ustaw w Excelu format kolumny na tekstowy.',
          modeAppend: 'Dołącz do listy',
          modeBody:
            'Na liście jest już {{existingCount}} numerów, a plik zawiera {{incomingCount}}. Dołączyć je do obecnej listy czy zastąpić ją nimi?',
          modeCancel: 'Anuluj',
          modeReplace: 'Zastąp listę',
          modeTitle: 'Wczytano plik z numerami SAP',
          rejectedRow: 'wiersz {{row}}: {{value}}',
          rejectedTitle: 'Odrzucone wpisy ({{rejectedCount}})',
          showAll: '+{{hiddenCount}} — pokaż wszystkie',
          showLess: 'Zwiń listę',
          summaryDuplicates: 'Pominięto duplikaty: {{duplicateCount}}.',
          summarySuccess: 'Wczytano {{addedCount}} numerów z pliku {{fileName}}.',
        },
        save: 'Zapisz',
        status: 'Status kampanii',
        statusHint: {
          [DevelopmentCycleCampaignStatusDto.Cancelled]:
            'Kampania anulowana. Jej plany są w historycznych, a pracownicy wrócili pod kampanię ogólną.',
          [DevelopmentCycleCampaignStatusDto.Draft]:
            'Kampania w przygotowaniu. Nie obejmuje jeszcze nikogo i nie tworzy zadań, więc te same numery SAP może mieć inny szkic.',
          [DevelopmentCycleCampaignStatusDto.Finished]:
            'Kampania zakończona. Plany z kompletem odhaczonych kroków są zamknięte jako zrealizowane, pozostałe trafiły do historycznych. Nie powstają nowe zadania.',
          [DevelopmentCycleCampaignStatusDto.InProgress]:
            'Kampania obejmuje pracowników i tworzy zadania. Numer SAP może należeć tylko do jednej takiej kampanii.',
        },
        statusOption: {
          [DevelopmentCycleCampaignStatusDto.Cancelled]: 'Anulowana',
          [DevelopmentCycleCampaignStatusDto.Draft]: 'Szkic',
          [DevelopmentCycleCampaignStatusDto.Finished]: 'Zakończona',
          [DevelopmentCycleCampaignStatusDto.InProgress]: 'W trakcie',
        },
        targetSaps: 'Numery SAP objętych pracowników',
        targetSapsAdd: 'Dodaj',
        targetSapsEmpty: 'Pusta lista — kampania nie obejmie nikogo i nie utworzy zadań.',
        targetSapsHint:
          'Pracownicy z tej listy mają zrobić plan w tej kampanii. Zadanie ustalenia kierunku dostaje ich przełożony P1, a po 7 dniach przechodzi na pracownika.',
        targetSapsPlaceholder: 'np. 10023',
        tasksStartDate: 'Data startu zadań',
      },
      impact: {
        activation:
          'Uruchomienie kampanii utworzy zadania ustalenia kierunku dla przełożonych P1 objętych pracowników i wyśle powiadomienia. Kampania ogólna nie tworzy żadnych zadań. Pozostałe trwające kampanie zostaną bez zmian. Kontynuować?',
        activeEdit:
          'Zapisanie zmian w trwającej kampanii anuluje otwarte zadania przedplanowe i utworzy je od nowa według nowej listy pracowników. Terminy 7 dni dla przełożonych zaczną biec od nowa, a powiadomienia zostaną wysłane ponownie. Kontynuować?',
        cancel: 'Anuluj',
        confirm: 'Kontynuuj',
        finish:
          'Zakończenie kampanii zamknie plany jej uczestników i nie utworzy już nowych zadań. Plan z wszystkimi krokami odhaczonymi zostanie zamknięty jako zrealizowany, a plan z niedokończonymi krokami trafi do historycznych i nie zaliczy etapu planu rozwoju w powiązanej certyfikacji. Tej zmiany nie da się cofnąć. Kontynuować?',
        title: 'Potwierdź skutki zmiany',
        withdrawal:
          'Kampania przestanie obejmować pracowników. Otwarte zadania przedplanowe zostaną anulowane, a założenie planu będzie możliwe tylko wtedy, gdy obejmie ich kampania ogólna. Istniejące plany i ich zadania pozostaną bez zmian. Kontynuować?',
      },
      members: {
        back: 'Wróć do kampanii',
        detail: {
          back: 'Wróć do listy',
          cycleCampaign: 'Kampania',
          cycleDirection: 'Kierunek',
          cycleState: 'Stan',
          cycleWindow: 'Okno',
          noAccount: 'Brak konta',
          noPlans: 'Ten pracownik nie ma jeszcze żadnego planu rozwojowego.',
          noPlansTitle: 'Brak planów',
          noTaskReason: 'Powód braku zadania',
          noTasks: 'Brak zadań związanych z planami tego pracownika.',
          noTasksTitle: 'Brak zadań',
          p1: 'Przełożony P1',
          pageDescription: 'Pełny podgląd planów i zadań jednego pracownika.',
          pageTitle: 'Szczegóły pracownika',
          plansTitle: 'Plany rozwojowe',
          taskDeadline: 'Termin',
          taskDescription: 'Opis',
          taskStatus: 'Status',
          tasksTitle: 'Zadania planów',
          taskTarget: 'Cel',
          taskTrigger: 'Typ',
          thisCampaign: 'Ta kampania',
        },
        emptyDescription: 'Żaden pracownik nie pasuje do wybranych filtrów.',
        emptyTitle: 'Brak pracowników',
        error: 'Nie udało się wczytać listy objętych pracowników.',
        errorTitle: 'Błąd',
        filters: {
          anyPlanStatus: 'Dowolny status planu',
          planStatusLabel: 'Status planu',
          searchPlaceholder: 'Szukaj po nazwisku lub SAP',
          withoutAccount: 'Bez konta użytkownika',
          withoutTask: 'Bez zadania',
        },
        no: 'Nie',
        pageDescription:
          'Pracownicy objęci kampanią wraz ze statusem planu, zadaniem i powodem jego braku.',
        pageTitle: 'Objęci pracownicy',
        planStatus: {
          ARCHIVED: 'Historyczny',
          CANCELLED: 'Anulowany',
          COMPLETED: 'Zrealizowany',
          DRAFT: 'Szkic',
          NONE: 'Brak planu',
          READY_FOR_REALIZATION: 'Gotowy do realizacji',
          SUPERVISOR_APPROVED: 'Zatwierdzony',
          SUPERVISOR_REJECTED: 'Odrzucony',
        },
        reason: {
          BEFORE_TASKS_START: 'Przed startem zadań kampanii',
          CAMPAIGN_NOT_IN_PROGRESS: 'Kampania nie jest w trakcie',
          CATCH_ALL: 'Kampania ogólna — nie tworzy zadań',
          NO_USER_ACCOUNT: 'Brak konta użytkownika',
          PENDING_SCAN: 'Czeka na najbliższy skan',
          PLAN_EXISTS: 'Plan już istnieje',
        },
        source: {
          APPLY_TO_ALL: 'Kampania ogólna',
          TARGETED: 'Z listy',
        },
        summary: {
          covered: 'Objętych',
          withOpenTask: 'Z zadaniem',
          withPlan: 'Z planem',
          withUserAccount: 'Z kontem',
        },
        table: {
          account: 'Konto',
          name: 'Pracownik',
          p1: 'Przełożony P1',
          plan: 'Status planu',
          source: 'Źródło',
          task: 'Zadanie / powód',
        },
        taskType: {
          PLAN_INITIATION: 'Inicjacja (P1)',
          PLAN_REQUIRED: 'Stwórz plan',
        },
        yes: 'Tak',
      },
      noCampaignInProgress:
        'Żadna kampania nie jest w trakcie — nikt nie jest objęty, więc tworzenie planów jest wyłączone, a zadania nie powstają.',
      sweep: {
        button: 'Uruchom tworzenie zadań teraz',
        description:
          'Odpala od ręki dokładnie to, co scheduler robi codziennie o 06:15: tworzy zadania ustalenia kierunku dla przełożonych objętych pracowników, a gdy kampanii brak — sprząta zalegające zadania.',
        result: 'Gotowe — utworzone zadania: {{created}}, anulowane zalegające: {{cancelled}}.',
        testBadge: 'narzędzie testowe',
        title: 'Scheduler zadań',
      },
      table: {
        actions: 'Akcje',
        campaignType: 'Typ',
        cancelAction: 'Anuluj kampanię',
        covered: 'Objęci',
        coveredAll: 'wszyscy',
        coveredEmpty: 'nikt',
        createButton: 'Nowa kampania',
        deleteAction: 'Usuń',
        downloadPlansAction: 'Pobierz plany rozwojowe objętych pracowników',
        downloadPlansColumn: 'Plany',
        editAction: 'Edytuj',
        empty: 'Nie skonfigurowano jeszcze żadnej kampanii cyklu rozwojowego.',
        emptyFiltered: 'Żadna kampania nie pasuje do filtrów. Zmień je albo wyczyść wyszukiwanie.',
        emptyFilteredTitle: 'Brak wyników filtrów',
        emptyTitle: 'Brak kampanii',
        finishAction: 'Zakończ',
        members: 'Objęci',
        membersAction: 'Pokaż objętych pracowników',
        name: 'Nazwa',
        startAction: 'Uruchom',
        status: 'Status',
        tasksStartDate: 'Start zadań',
        title: 'Wszystkie kampanie',
        window: 'Okno cyklu',
      },
    },
    employees: {
      description: 'Wyszukuj pracowników i zarządzaj ich aktywnością w organizacji.',
    },
    legalDocuments: {
      description: 'Publikuj regulamin, który użytkownicy otwierają ze stopki aplikacji.',
    },
    permissions: {
      description: 'Zarządzaj rolami, uprawnieniami i dostępem użytkowników w systemie.',
    },
  },
  sidebar: {
    backToStart: 'Powrót do startu',
    collapseAriaLabelCollapsed: 'Rozwiń menu administracyjne',
    collapseAriaLabelExpanded: 'Zwiń menu administracyjne',
    subtitle: 'Sekcje administracyjne',
  },
  structureBrowser: {
    axis: {
      hrbp: 'Po HR biznes partnerze',
      label: 'Grupuj',
      supervisor: 'Po przełożonym',
    },
    backToList: 'Wróć do listy',
    columns: {
      department: 'Dział',
      name: 'Osoba',
      peopleCount: 'Osoby z kontem',
      position: 'Stanowisko',
      roles: 'Role',
      sapNumber: 'Numer SAP',
    },
    description: {
      accounts:
        'Widoczne są tylko osoby z aktywnym kontem i przynajmniej jedną rolą, więc liczby mogą być mniejsze niż w Strukturze.',
      scopeHrbp: 'Liczba obejmuje osoby przypisane bezpośrednio do danej osoby.',
      scopeSupervisor:
        'Liczba obejmuje całą strukturę pod daną osobą, nie tylko bezpośrednich podwładnych.',
    },
    empty: {
      noPeople: 'Nie ma tu nikogo z aktywnym kontem.',
      noSupervisors: 'Nikt tutaj nie ma przypisanej osoby z aktywnym kontem.',
    },
    team: {
      title: 'Osoby podlegające: {{name}}',
      total: 'Osób: {{total}}',
    },
    title: 'Przeglądarka struktury',
  },
  successionPermissions: {
    comingSoon: 'Wkrótce dostępne',
    description: 'Tutaj będzie dostępne zarządzanie uprawnieniami do sukcesji.',
    errors: {
      loadingData: 'Błąd podczas ładowania danych:',
      loadingUnits: 'Błąd podczas ładowania jednostek:',
    },
    loading: {
      data: 'Ładowanie danych...',
      units: 'Ładowanie jednostek...',
    },
    messages: {
      hrbpHqInfo: 'Tutaj widoczni są tylko użytkownicy posiadający rolę HRBP HQ.',
      noDataForSelectedUnits: 'Brak danych do wyświetlenia dla wybranych jednostek.',
      saving: 'Zapisywanie...',
      selectUnits: 'Wybierz jednostki organizacyjne, aby wyświetlić uprawnienia HRBP.',
    },
    table: {
      headers: {
        employeeCount: 'Liczba osób',
        unitPosition: 'Jednostka / Stanowisko',
      },
    },
    unitSelection: {
      allButton: 'Wszystkie',
      clearAllButton: 'Wyczyść wszystkie',
      label: 'Wybierz jednostki',
      moreChipLabel: 'więcej',
      placeholder: 'Wybierz jednostki organizacyjne',
      remainingUnitsTooltip: 'Pozostałe jednostki',
    },
  },
  surveyDetail: {
    backButton: 'Powrót',
    backToCertification: 'Wróć do certyfikatu',
    backToSurveys: 'Wróć do ankiet',
    createdAt: 'Utworzona',
    createdBy: 'Autor',
    deadline: 'Deadline',
    editButton: 'Edytuj',
    label: 'Ankieta',
    overview: {
      areas: 'Obszary',
      categories: 'Kategorie',
      categoryLink: 'Linki',
      closedQuestions: 'Zamknięte',
      negative: 'Negatywna',
      noCategoriesFound: 'Nie znaleziono kategorii',
      openQuestions: 'Otwarte',
      positive: 'Pozytywna',
      procedureDescription: 'Opis procedury',
      procedureName: 'Procedura',
      questions: 'Pytania',
      scale: 'Skala ocen',
      searchCategories: 'Szukaj kategorii...',
      selectCategoryPrompt: 'Wybierz kategorię, aby zobaczyć szczegóły',
      threshold: 'próg',
      total: 'Łącznie',
    },
    participants: {
      actions: 'Akcje',
      answers: {
        allAreas: 'Wszystkie obszary',
        allCategories: 'Wszystkie kategorie',
        answer: 'Odpowiedź',
        answerDuration: 'Czas: {{duration}}',
        areaFilter: 'Obszar',
        categoryFilter: 'Kategoria',
        error: 'Błąd podczas ładowania odpowiedzi',
        loading: 'Ładowanie odpowiedzi...',
        noData: 'Brak odpowiedzi',
        questionNumber: 'Pytanie {{number}}',
        report: {
          downloadButton: 'Pobierz raport Excel',
          downloading: 'Pobieranie...',
          error: 'Błąd podczas pobierania raportu',
          reportType: {
            EMPLOYEE_SURVEY_ANSWERS: 'Odpowiedzi pracownika z ankiety',
          },
          subtitle: 'Pobierz pełny raport Excel z odpowiedziami i kategoriami poniżej progu.',
          title: 'Raport odpowiedzi',
        },
        rowsPerPage: 'Wierszy na stronę',
        searchPlaceholder: 'Szukaj w pytaniach lub odpowiedziach...',
        summary: {
          allCategoriesMet: 'Nie ma obszarów wymagających poprawy.',
          categoriesCount: 'Poniżej znajdziesz obszary nad którymi warto pracować',
          competencyWorkRequired: 'Wymagana praca nad kompetencjami',
          goToResources: 'Przejdź do zasobów',
          greatJob: 'Świetna robota!',
          howToImprove: 'Co robić, aby zwiększyć swoją wiedzę?',
          loading: 'Ładowanie podsumowania...',
          procedureDescription: 'Opis procedury',
          procedureName: 'Procedura',
          questionNumber: 'Pytanie {{number}}:',
          supportMaterials: 'Materiały pomocnicze:',
          viewAnswers: 'Zobacz odpowiedzi',
        },
        tabAnswers: 'Odpowiedzi uczestnika',
        tabReport: 'Raport odpowiedzi',
        tabSummary: 'Podsumowanie ankiety pracownika',
        title: 'Odpowiedzi uczestnika',
        totalAnswers: 'Łącznie odpowiedzi: {{count}}',
      },
      assignedAt: 'Przypisano',
      completedAt: 'Ukończono',
      dates: 'Daty',
      details: 'Szczegóły',
      email: 'Email',
      hasSurveyAssigned: 'Ankieta przypisana',
      name: 'Imię i nazwisko',
      notificationSent: 'Powiadomienie wysłane',
      position: 'Stanowisko',
      search: 'Szukaj',
      startedAt: 'Rozpoczęto',
      status: {
        all: 'Wszystkie',
        null: 'Brak',
        [SurveyAssignmentStatusDto.Cancelled]: 'Anulowana',
        [SurveyAssignmentStatusDto.Completed]: 'Ukończona',
        [SurveyAssignmentStatusDto.InProgress]: 'W trakcie',
        [SurveyAssignmentStatusDto.Pending]: 'Oczekuje',
      },
      statusLabel: 'Status',
      table: {
        actions: 'Akcje',
        error: 'Błąd podczas ładowania uczestników',
        loading: 'Ładowanie uczestników...',
        noData: 'Brak uczestników',
        rowsPerPage: 'Wierszy na stronę',
      },
      viewAnswers: 'Zobacz odpowiedzi',
    },
    questions: {
      all: 'Wszystkie',
      area: 'Obszar',
      assignedTo: 'Przypisane do',
      category: 'Kategoria',
      categoryDetails: 'Szczegóły kategorii',
      categoryLink: 'Otwórz link',
      employee: 'Pracownik',
      filters: 'Filtr',
      group: 'Grupa',
      position: 'Stanowisko',
      procedureDescription: 'Opis procedury',
      procedureName: 'Procedura',
      total: 'Łącznie',
      type_CLOSED: 'Zamknięte',
      type_OPEN: 'Otwarte',
    },
    reports: {
      download: 'Pobierz',
      downloading: 'Pobieranie...',
      error: 'Błąd podczas pobierania raportu',
      reportType: {
        EMPLOYEE_SURVEY_ANSWERS: 'Odpowiedzi pracowników z ankiety',
      },
      subtitle: 'Pobierz raporty dla wszystkich uczestników ankiety.',
    },
    status: 'Status',
    statusAction: {
      cancel: 'Anuluj ankietę',
      complete: 'Zakończ',
      start: 'Uruchom',
    },
    statusUpdateError: 'Błąd podczas zmiany statusu',
    statusUpdateSuccess: 'Status zaktualizowany',
    tabs: {
      overview: 'Przegląd',
      participants: 'Uczestnicy',
      questions: 'Pytania',
      reports: 'Raporty',
    },
  },
  surveys: {
    addButton: 'Dodaj ankietę',
    dialog: {
      cancel: 'Anuluj',
      deadline: 'Deadline',
      deadlineHelp: 'Opcjonalna data zakończenia ankiety',
      dragDropText: 'lub przeciągnij tutaj',
      file: 'Plik Excel',
      fileHelp: 'Plik powinien zawierać arkusze: Pytania, Skale, Kategorie',
      name: 'Nazwa ankiety',
      save: 'Zapisz',
      saving: 'Wysyłanie...',
      title: 'Dodaj nową ankietę',
      uploadSuccess: 'Ankieta została pomyślnie przesłana',
    },
    editDialog: {
      cancel: 'Anuluj',
      deadline: 'Deadline',
      deadlineHelp: 'Opcjonalna data zakończenia ankiety',
      deadlineMustBeFuture: 'Termin musi być w przyszłości',
      name: 'Nazwa ankiety',
      nameMaxLength: 'Nazwa może mieć maksymalnie 50 znaków',
      nameRequired: 'Nazwa ankiety jest wymagana',
      save: 'Zapisz',
      saving: 'Zapisywanie...',
      status: 'Status',
      statusLocked: 'Statusu anulowanej ankiety nie można zmienić.',
      title: 'Edytuj ankietę',
      updateError: 'Nie udało się zapisać zmian. Spróbuj ponownie.',
      updateSuccess: 'Ankieta została zaktualizowana',
    },
    filters: {
      search: 'Szukaj',
    },
    status: {
      ACTIVE: 'Aktywna',
      CANCELLED: 'Anulowana',
      COMPLETED: 'Zakończona',
      DRAFT: 'Szkic',
    },
    statusUpdateError: 'Błąd podczas aktualizacji statusu',
    statusUpdateSuccess: 'Status ankiety został zaktualizowany',
    table: {
      actions: 'Akcje',
      author: 'Autor',
      createdAt: 'Utworzona',
      deadline: 'Termin',
      details: 'Szczegóły',
      edit: 'Edytuj',
      error: 'Błąd podczas ładowania ankiet',
      loading: 'Ładowanie ankiet...',
      name: 'Nazwa',
      noData: 'Brak dostępnych ankiet',
      participants: 'Uczestnicy',
      report: 'Raport',
      rowsPerPage: 'Wierszy na stronę',
      status: 'Status',
    },
    title: 'Zarządzaj ankietami',
  },
  tabs: {
    accountsManagement: 'Konta',
    certificationSubTabs: {
      certification: 'Certyfikaty',
      questionPools: 'Pule pytań',
      surveys: 'Ankiety',
    },
    changeHistory: 'Historia zmian',
    dataManagement: 'Dane',
    dataManagementUpload: 'Wgraj dane',
    developmentCycles: 'Cykle rozwojowe',
    devPlanTester: 'Tester planu (dev)',
    dictionaries: 'Słowniki',
    employeesManagement: 'Pracownicy',
    impersonation: 'Przełącz użytkownika',
    legalDocuments: 'Dokumenty prawne',
    news: 'Aktualności',
    permissions: 'Role i uprawnienia',
    permissionSubTabs: {
      assignments: 'Przypisania',
      employees: 'Pracownicy',
      hrbpPermissions: 'Uprawnienia HRBP HQ',
      permissions: 'Uprawnienia',
      roles: 'Role',
    },
    professionalActivityDefinitions: 'Aktywności rozwojowe',
    ragDocuments: 'Dokumenty czatu',
    structureBrowser: 'Przeglądarka struktury',
    successionPermissions: 'Uprawnienia (sukcesja)',
    surveys: 'Certyfikacja',
    taskCreator: 'Tworzenie zadania (dev)',
  },
  taskCreator: {
    creating: 'Tworzenie...',
    deadline: 'Termin wykonania',
    description: 'Opis',
    error: 'Nie udało się utworzyć zadania.',
    resourceType: {
      [ResourceTypeDto.App]: 'Aplikacja',
      [ResourceTypeDto.DevelopmentPath]: 'Ścieżka rozwoju',
      [ResourceTypeDto.Employee]: 'Pracownik',
      [ResourceTypeDto.Survey]: 'Ankieta',
    },
    sourceResourceType: 'Typ źródła',
    sourceResourceValue: 'Wartość źródła',
    submit: 'Utwórz zadanie',
    subtitle:
      'Narzędzie do testów. Zadanie przypisuje się do Ciebie, a cel pracownika używa SAP ID.',
    targetResourceType: 'Typ celu',
    targetResourceValue: 'Wartość celu (SAP / zewnętrzne ID)',
    title: 'Tworzenie zadania',
  },
  title: 'Panel Admina',
};
